import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  useTheme,
  alpha,
  Fade,
  Zoom,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Snackbar,
  ToggleButton,
  Skeleton,
  Badge,
  LinearProgress,
  Slide,
  Grow,
  Collapse,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Switch,
  FormControlLabel,
  Stack
} from '@mui/material';
import {
  Add,
  FilterList,
  Search,
  ViewKanban,
  Assignment,
  BugReport,
  Task,
  Person,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  Star,
  StarBorder,
  CheckCircle,
  Warning,
  Error,
  Info,
  FilterAlt,
  Clear,
  Refresh,
  Close as CloseIcon,
  DragIndicator,
  MoreHoriz,
  AccessTime,
  PriorityHigh,
  Flag,
  Label,
  Group,
  TrendingUp,
  Speed,
  AutoAwesome,
  PersonOff,
  Timeline as TimelineIcon,
  ChatBubbleOutline,
  Traffic
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { Issue, User, IssueType, IssueStatus, Priority, Comment } from '../types';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import IssueDetailsModal from '../components/Common/IssueDetailsModal';
import CreateIssueModal from '../components/Common/CreateIssueModal';
import Layout from '../components/Layout/Layout';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SprintTimelineView from '../components/Sprint/SprintTimelineView';

const DEFAULT_RAG_SETTINGS = {
  enabled: true,
  amberThresholdDays: 3,
  redGracePeriodDays: 0,
} as const;

const RAG_SORT_ORDER: Record<'red' | 'amber' | 'green', number> = {
  red: 0,
  amber: 1,
  green: 2
};

interface BoardFilters {
  assignees: string[];
  priorities: string[];
  types: string[];
  statuses: string[];
  search: string;
  dueDateFrom: string;
  dueDateTo: string;
  ragStatuses: Array<'red' | 'amber' | 'green'>;
}

type TableSortField = 'issueKey' | 'title' | 'type' | 'status' | 'dueDate' | 'assignee' | 'ragStatus' | 'priority' | 'storyPoints' | 'lastComment';

type RagSettingsFormState = {
  amberThresholdDays: string;
  redGracePeriodDays: string;
  enabled: boolean;
};

const Board: React.FC = () => {
  const theme = useTheme();
  const ragStatusOptions = React.useMemo(
    () => [
      { value: 'red' as const, label: 'Red', color: theme.palette.error.main },
      { value: 'amber' as const, label: 'Amber', color: theme.palette.warning.main },
      { value: 'green' as const, label: 'Green', color: theme.palette.success.main }
    ],
    [theme]
  );
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const boardId = searchParams.get('boardId');

  

  
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [project, setProject] = useState<any>(null);
  const [board, setBoard] = useState<any>(null);
  const [currentSprint, setCurrentSprint] = useState<any>(null);
  const [currentFeature, setCurrentFeature] = useState<any>(null);
  const [showAddStoryModal, setShowAddStoryModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [filters, setFilters] = useState<BoardFilters>({
    assignees: [],
    priorities: [],
    types: [],
    statuses: [],
    search: '',
    dueDateFrom: '',
    dueDateTo: '',
    ragStatuses: []
  });

  const handleToggleRagStatus = React.useCallback((status: 'red' | 'amber' | 'green') => {
    setFilters(prev => {
      const isSelected = prev.ragStatuses.includes(status);
      const next = isSelected
        ? prev.ragStatuses.filter(val => val !== status)
        : [...prev.ragStatuses, status];
      return { ...prev, ragStatuses: next };
    });
  }, []);

  const handleClearRagStatuses = React.useCallback(() => {
    setFilters(prev => ({ ...prev, ragStatuses: [] }));
  }, []);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  const [sprintDetailsForTimeline, setSprintDetailsForTimeline] = useState<any>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [draggedIssue, setDraggedIssue] = useState<Issue | null>(null);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [editColumnName, setEditColumnName] = useState('');
  const [customColumns, setCustomColumns] = useState<{[key: string]: {title: string, color: string, icon: any}}>(() => {
    // Load custom columns from localStorage on component mount
    const saved = localStorage.getItem(`board-columns-${projectId}`);
    return saved ? JSON.parse(saved) : {};
  });
  const [viewMode, setViewMode] = useState<'board' | 'table'>('board');
  const [tableSort, setTableSort] = useState<{ field: TableSortField; direction: 'asc' | 'desc' }>({
    field: 'status',
    direction: 'asc'
  });
  const [editingIssueId, setEditingIssueId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<{
    status?: string;
    assigneeId?: string;
    priority?: Priority;
    storyPoints?: string;
    dueDate?: string;
  }>({});
  const [savingIssueId, setSavingIssueId] = useState<string | null>(null);
const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
const [commentsDialogIssue, setCommentsDialogIssue] = useState<Issue | null>(null);
const [commentsLoading, setCommentsLoading] = useState(false);
const [issueComments, setIssueComments] = useState<Comment[]>([]);
const [newComment, setNewComment] = useState('');
const [commentsCache, setCommentsCache] = useState<Record<string, { lastComment: string; lastUpdated: string }>>({});
const [ragSettingsDialogOpen, setRagSettingsDialogOpen] = useState(false);
const [ragSettingsForm, setRagSettingsForm] = useState<RagSettingsFormState>({
  amberThresholdDays: DEFAULT_RAG_SETTINGS.amberThresholdDays.toString(),
  redGracePeriodDays: DEFAULT_RAG_SETTINGS.redGracePeriodDays.toString(),
  enabled: DEFAULT_RAG_SETTINGS.enabled
});
const [savingRagSettings, setSavingRagSettings] = useState(false);

  useEffect(() => {
    if (projectId) {
      loadBoardData();
    }
  }, [projectId]);

  const ragSettings = React.useMemo(() => ({
    enabled: board?.config?.ragSettings?.enabled ?? DEFAULT_RAG_SETTINGS.enabled,
    amberThresholdDays: board?.config?.ragSettings?.amberThresholdDays ?? DEFAULT_RAG_SETTINGS.amberThresholdDays,
    redGracePeriodDays: board?.config?.ragSettings?.redGracePeriodDays ?? DEFAULT_RAG_SETTINGS.redGracePeriodDays,
  }), [board?.config?.ragSettings]);

  useEffect(() => {
    setRagSettingsForm({
      enabled: ragSettings.enabled,
      amberThresholdDays: (ragSettings.amberThresholdDays ?? DEFAULT_RAG_SETTINGS.amberThresholdDays).toString(),
      redGracePeriodDays: (ragSettings.redGracePeriodDays ?? DEFAULT_RAG_SETTINGS.redGracePeriodDays).toString()
    });
  }, [ragSettings]);

  // Listen for story creation events (from Issues page or other tabs) and refresh board
  useEffect(() => {
    const handleStoryCreated = (e: any) => {
      const evtProjectId = e?.detail?.projectId;
      if (!projectId || (evtProjectId && evtProjectId !== projectId)) return;
      loadBoardData();
    };
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'storyCreatedEvent') {
        try {
          const data = e.newValue ? JSON.parse(e.newValue) : null;
          if (!projectId || (data?.projectId && data.projectId !== projectId)) return;
          loadBoardData();
        } catch {}
      }
    };
    window.addEventListener('storyCreated', handleStoryCreated as any);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storyCreated', handleStoryCreated as any);
      window.removeEventListener('storage', handleStorage);
    };
  }, [projectId]);

  const loadBoardData = async () => {
    try {
      setLoading(true);
      
      let boardData: any;
      if (boardId) {
        // Load specific board
        boardData = await apiService.getBoard(projectId!, boardId);
      } else {
        // Load default board for project
        boardData = await apiService.getBoard(projectId!);
      }
      
      const [boardMembersData, projectData, sprintsData, projectAssignmentsData] = await Promise.all([
        boardData.board?.id ? apiService.getBoardMembers(boardData.board.id) : [],
        apiService.getProject(projectId!),
        apiService.getSprints(projectId!),
        apiService.getProjectAssignments(projectId!)
      ]);

      // Extract user data from board members
      const usersData = Array.isArray(boardMembersData) 
        ? boardMembersData.map((member: any) => member.user).filter(Boolean)
        : [];

      // Extract user data from project assignments
      const projectUsersData = Array.isArray(projectAssignmentsData)
        ? projectAssignmentsData.map((assignment: any) => assignment.user).filter(Boolean)
        : [];

      // Calculate available users (project members who are not yet board members)
      const boardMemberIds = usersData.map(user => user.id);
      const availableUsersData = projectUsersData.filter(user => !boardMemberIds.includes(user.id));
      
      console.log('🔍 Board members data:', boardMembersData);
      console.log('🔍 Extracted users data:', usersData);
      console.log('🔍 Project assignments data:', projectAssignmentsData);
      console.log('🔍 Available users data:', availableUsersData);

      const allIssues = Object.values(boardData.issuesByStatus).flat() as Issue[];
      console.log('Board data loaded:', boardData);
      console.log('Board object:', boardData.board);
      console.log('Board columns:', boardData.board?.columns);
      console.log('All issues from board:', allIssues);
      console.log('🔍 All issues after refresh:', allIssues.map(issue => ({
        id: issue.id,
        title: issue.title,
        assigneeId: issue.assigneeId,
        assignments: issue.assignments?.map(a => ({ userId: a.userId, user: a.user?.firstName + ' ' + a.user?.lastName }))
      })));
      console.log('Issues by status:', boardData.issuesByStatus);
      
      // Debug: Check custom columns specifically
      if (boardData.board?.columns) {
        boardData.board.columns.forEach((col: any) => {
          const standardStatuses = ['backlog', 'todo', 'in_progress', 'in_review', 'testing', 'done', 'closed', 'cancelled'];
          if (!standardStatuses.includes(col.status)) {
            console.log(`🔍 Custom column "${col.name}" (status: ${col.status}, id: ${col.id})`);
            console.log(`🔍 Issues for this column:`, boardData.issuesByStatus[col.status] || []);
          }
        });
      }
      
      setIssues(allIssues);
      const hydratedCommentsCache: Record<string, { lastComment: string; lastUpdated: string }> = {};
      allIssues.forEach(issue => {
        const latestComment =
          issue.comments && issue.comments.length > 0
            ? issue.comments[issue.comments.length - 1]
            : undefined;
        hydratedCommentsCache[issue.id] = {
          lastComment: latestComment?.content || '',
          lastUpdated: latestComment?.updatedAt || latestComment?.createdAt || ''
        };
      });
      setCommentsCache(hydratedCommentsCache);
      setUsers(usersData);
      setAvailableUsers(availableUsersData);
      setProject(projectData);
      setBoard(boardData.board);
      
      console.log('🔍 State set - allIssues:', allIssues.length, 'board:', boardData.board?.name);
      
      console.log('🔍 Board Debug - Final state:', {
        boardMembers: usersData,
        availableUsers: availableUsersData,
        projectAssignments: projectUsersData
      });
      
      // For sprint boards, find the sprint linked to this board
      let activeSprint = null;
      if (boardData.board?.type === 'sprint' && boardData.board?.id) {
        // Get sprints linked to this sprint board
        try {
          const boardSprints = await apiService.getBoardSprints(boardData.board.id);
          if (boardSprints && boardSprints.length > 0) {
            // Use the first sprint linked to this board (usually there's only one)
            activeSprint = boardSprints[0];
            console.log('🔍 Found sprint linked to board:', activeSprint);
          }
        } catch (err) {
          console.warn('Could not fetch board sprints:', err);
        }
        
        // Fallback: If no sprint linked to board, try to find an active sprint for this project
        if (!activeSprint && sprintsData && sprintsData.length > 0) {
          // Try to find an active sprint
          activeSprint = sprintsData.find((sprint: any) => sprint.isActive === true) ||
                       sprintsData.find((sprint: any) => sprint.state === 'ACTIVE') ||
                       sprintsData.find((sprint: any) => sprint.state === 'active');
          
          // If still no active sprint, use the most recent sprint
          if (!activeSprint && sprintsData.length > 0) {
            activeSprint = sprintsData[0];
          }
          
          if (activeSprint) {
            console.log('🔍 Using fallback sprint for sprint board:', activeSprint);
          }
        }
        
        // For sprint boards, set the sprint (even if null, timeline button will handle it)
        setCurrentSprint(activeSprint);
      } else {
        // For non-sprint boards (kanban/scrum/bau), clear currentSprint
        setCurrentSprint(null);
      }
      
      // For now, we'll use the first feature or create a default one
      // In a real implementation, you'd get this from the board or sprint context
      setCurrentFeature({
        id: 'default-feature',
        name: 'Default Feature',
        description: 'Default feature for board issues'
      });
      
      console.log('Board data loaded:', boardData.board);
      console.log('All sprints:', sprintsData);
      console.log('Current sprint:', activeSprint);
    } catch (error) {
      console.error('Error loading board data:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load board data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleIssueUpdate = (updatedIssue: Issue) => {
    console.log('🔄 handleIssueUpdate called with:', updatedIssue);
    console.log('🔄 Updated issue assigneeId:', updatedIssue.assigneeId);
    console.log('🔄 Updated issue assignments:', updatedIssue.assignments);
    console.log('🔄 Refreshing board data...');
    loadBoardData();
    setSnackbar({
      open: true,
      message: 'Issue updated successfully',
      severity: 'success'
    });
  };

  const handleIssueClick = (issue: Issue) => {
    setSelectedIssue(issue);
    setModalOpen(true);
  };

  const handleRowPrimaryAction = (issue: Issue, e: React.MouseEvent) => {
    e.stopPropagation();
    if (editingIssueId === issue.id) {
      if (savingIssueId === issue.id) return;
      saveTableEdit(issue);
    } else {
      handleIssueClick(issue);
    }
  };

  const handleChildOpenIssue = (issue: Issue, e: React.MouseEvent) => {
    e.stopPropagation();
    handleIssueClick(issue);
  };

  const handleDragStart = (e: React.DragEvent, issue: Issue) => {
    console.log('🔍 Drag started for issue:', issue.title, issue.id);
    setDraggedIssue(issue);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
    
    // Add smooth drag feedback
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '0.5';
    target.style.transform = 'rotate(2deg) scale(1.02)';
    target.style.transition = 'all 0.2s ease';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Add visual feedback for drop zone
    const target = e.currentTarget as HTMLElement;
    target.style.borderColor = theme.palette.primary.main;
    target.style.backgroundColor = alpha(theme.palette.primary.main, 0.05);
    target.style.transform = 'scale(1.02)';
    
    console.log('🔍 Drag over detected, draggedIssue:', draggedIssue?.title);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    // Reset drag styles
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '1';
    target.style.transform = 'rotate(0deg) scale(1)';
    target.style.transition = 'all 0.2s ease';
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Reset drop zone styles
    const target = e.currentTarget as HTMLElement;
    target.style.borderColor = '';
    target.style.backgroundColor = '';
    target.style.transform = '';
  };

  const getStatusFromKey = (statusKey: string): string => {
    // Map status keys to enum values
    switch (statusKey) {
      case 'todo': return IssueStatus.TODO;
      case 'in-progress': return IssueStatus.IN_PROGRESS;
      case 'review': return IssueStatus.IN_REVIEW;
      case 'done': return IssueStatus.DONE;
      case 'backlog': return IssueStatus.BACKLOG;
      case 'testing': return IssueStatus.TESTING;
      case 'closed': return IssueStatus.CLOSED;
      default: 
        // For custom columns, return the status key as-is (it's already the custom status)
        return statusKey;
    }
  };

  const handleDrop = async (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    if (!draggedIssue) return;

    console.log('🔍 Debug: handleDrop called with:', { targetColumnId, draggedIssue });

    // Find the target column by column ID
    const targetColumn = board?.columns?.find((col: any) => col.id === targetColumnId);
    
    if (!targetColumn) {
      console.error('Target column not found for columnId:', targetColumnId);
      console.error('Available columns:', board?.columns?.map((col: any) => ({ id: col.id, name: col.name, status: col.status })));
      return;
    }

    console.log('🔍 Debug: Target column found:', { 
      colName: targetColumn.name, 
      colStatus: targetColumn.status, 
      colId: targetColumn.id
    });
    
    console.log('🔍 Debug: targetColumn found:', targetColumn);
    
    let updatedIssue = { ...draggedIssue, status: targetColumn.status };
    
    // Always add the column ID to custom fields for proper linking
    updatedIssue = {
      ...updatedIssue,
      customFields: {
        ...updatedIssue.customFields,
        columnId: targetColumn.id
      }
    };
    
    // Update the issues state immediately for smooth UX
    setIssues(prevIssues => 
      prevIssues.map(issue => 
        issue.id === draggedIssue.id ? updatedIssue : issue
      )
    );

    try {
      // Make the API call in background
      const updateData: any = { 
        status: targetColumn.status,
        customFields: {
          ...draggedIssue.customFields,
          columnId: targetColumn.id
        }
      };
      
      console.log('🔍 Debug: API call with columnId:', updateData);
      
      await apiService.updateIssue(draggedIssue.id, updateData);
      
      // Show success message without reloading
      setSnackbar({
        open: true,
        message: `Issue moved to ${targetColumn.name}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating issue status:', error);
      
      // Revert the optimistic update on error
      setIssues(prevIssues => 
        prevIssues.map(issue => 
          issue.id === draggedIssue.id ? draggedIssue : issue
        )
      );
      
      setSnackbar({
        open: true,
        message: 'Failed to move issue',
        severity: 'error'
      });
    } finally {
      setDraggedIssue(null);
    }
  };

  // Column drag and drop handlers
  const handleColumnDragStart = (e: React.DragEvent, status: string) => {
    setDraggedColumn(status);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', status);
    
    // Add smooth column drag feedback
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '0.7';
    target.style.transform = 'scale(1.05)';
    target.style.transition = 'all 0.2s ease';
  };

  const handleColumnDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleColumnDragEnd = (e: React.DragEvent) => {
    // Reset column drag styles
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '1';
    target.style.transform = 'scale(1)';
    target.style.transition = 'all 0.2s ease';
  };

  const handleColumnDrop = async (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    console.log('Column drop event:', { draggedColumn, targetStatus });
    
    if (!draggedColumn || draggedColumn === targetStatus) {
      console.log('Invalid drop - same column or no dragged column');
      setDraggedColumn(null);
      return;
    }

    try {
      console.log('Board data:', board);
      console.log('Board columns:', board?.columns);
      
      if (!board?.columns) {
        console.log('No board columns found');
        return;
      }
      
      // Find columns by status (convert back from kebab-case to lowercase with underscores)
      const draggedStatus = draggedColumn.replace(/-/g, '_');
      const targetStatusEnum = targetStatus.replace(/-/g, '_');
      
      const draggedColumnObj = board.columns.find((col: any) => col.status === draggedStatus);
      const targetColumnObj = board.columns.find((col: any) => col.status === targetStatusEnum);
      
      console.log('Dragged column obj:', draggedColumnObj);
      console.log('Target column obj:', targetColumnObj);
      
      if (!draggedColumnObj || !targetColumnObj) {
        console.log('Column objects not found');
        return;
      }
      
      const draggedIndex = board.columns.findIndex((col: any) => col.status === draggedStatus);
      const targetIndex = board.columns.findIndex((col: any) => col.status === targetStatusEnum);
      
      console.log('Dragged index:', draggedIndex, 'Target index:', targetIndex);
      
      if (draggedIndex === -1 || targetIndex === -1) {
        console.log('Invalid indices');
        return;
      }
      
      // Create new column order
      const newColumns = [...board.columns];
      const [movedColumn] = newColumns.splice(draggedIndex, 1);
      newColumns.splice(targetIndex, 0, movedColumn);
      
      // Update order indices in the new columns array
      const updatedColumns = newColumns.map((col: any, index: number) => ({
        ...col,
        orderIndex: index
      }));
      
      // Update order indices for API call
      const columnOrders = updatedColumns.map((col: any) => ({
        id: col.id,
        orderIndex: col.orderIndex
      }));
      
      console.log('Column orders:', columnOrders);
      console.log('Board ID:', board.id);
      
      // Optimistic update - immediately update the UI with correct orderIndex values
      setBoard((prevBoard: any) => ({
        ...prevBoard,
        columns: updatedColumns
      }));
      
      // Call API to reorder columns in background
      await apiService.reorderColumns(board.id, columnOrders);
      
      setSnackbar({
        open: true,
        message: `Column moved successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error moving column:', error);
      
      // Revert optimistic update on error
      await loadBoardData();
      
      setSnackbar({
        open: true,
        message: 'Failed to move column',
        severity: 'error'
      });
    } finally {
      setDraggedColumn(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'todo': return 'default';
      case 'in-progress': return 'warning';
      case 'review': return 'info';
      case 'done': return 'success';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getIssueTypeIcon = (type: string) => {
    switch (type) {
      case 'story': return <Assignment />;
      case 'bug': return <BugReport />;
      case 'task': return <Task />;
      case 'subtask': return <Task />;
      default: return <Assignment />;
    }
  };

  const getIssueTypeColor = (type: string) => {
    switch (type) {
      case 'story': return 'primary';
      case 'bug': return 'error';
      case 'task': return 'info';
      case 'subtask': return 'secondary';
      default: return 'default';
    }
  };

  const getIssueTypeLabel = (type: string) => {
    switch (type) {
      case 'story': return 'Stories';
      case 'task': return 'Tasks';
      case 'subtask': return 'Subtasks';
      case 'bug': return 'Bugs';
      default: return type;
    }
  };

  const handleViewModeChange = (_event: React.SyntheticEvent, value: 'board' | 'table') => {
    setViewMode(value);
  };

  const handleTableSort = (field: TableSortField) => {
    setTableSort((prev) => {
      if (prev.field === field) {
        return {
          field,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { field, direction: 'asc' };
    });
  };

  const getIssueKeyDisplay = (issue: Issue): string => {
    if (issue.timeTrackerTaskId && issue.timeTrackerTaskId.trim() !== '') {
      return issue.timeTrackerTaskId;
    }
    if (issue.issueKey && issue.issueKey.trim() !== '') {
      return issue.issueKey;
    }
    const prefix = issue.project?.storyPrefix || issue.project?.key;
    if (prefix) {
      return `${prefix}-${issue.issueNumber}`;
    }
    return `#${issue.issueNumber}`;
  };

  const getIssueAssigneeNames = (issue: Issue): string => {
    const names: string[] = [];

    if (issue.assignee) {
      const fullName = `${issue.assignee.firstName || ''} ${issue.assignee.lastName || ''}`.trim();
      if (fullName) {
        names.push(fullName);
      }
    } else if (issue.assigneeId) {
      const fallbackUser = users.find((u) => u.id === issue.assigneeId);
      if (fallbackUser) {
        const fullName = `${fallbackUser.firstName || ''} ${fallbackUser.lastName || ''}`.trim();
        if (fullName) {
          names.push(fullName);
        }
      }
    }

    if (issue.assignments && issue.assignments.length > 0) {
      issue.assignments
        .filter((assignment) => assignment.isActive && assignment.user)
        .forEach((assignment) => {
          const fullName = `${assignment.user?.firstName || ''} ${assignment.user?.lastName || ''}`.trim();
          if (fullName && !names.includes(fullName)) {
            names.push(fullName);
          }
        });
    }

    if (names.length === 0) {
      return 'Unassigned';
    }

    return names.join(', ');
  };

  const formatDisplayDate = (value?: string): string => {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    return date.toLocaleDateString();
  };

  const computeRagStatus = React.useCallback((issue: Issue): 'red' | 'amber' | 'green' => {
    const doneStatuses = new Set<IssueStatus>([
      IssueStatus.DONE,
      IssueStatus.CLOSED,
      IssueStatus.CANCELLED
    ]);

    if (!ragSettings.enabled) {
      return 'green';
    }

    if (!issue || doneStatuses.has(issue.status as IssueStatus)) {
      return 'green';
    }

    if (issue.isOverdue) {
      return 'red';
    }

    if (!issue.dueDate) {
      return 'green';
    }

    const dueDate = new Date(issue.dueDate);
    if (Number.isNaN(dueDate.getTime())) {
      return 'green';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    const diffDays = Math.floor((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const amberThreshold = Math.max(0, ragSettings.amberThresholdDays ?? DEFAULT_RAG_SETTINGS.amberThresholdDays);
    const redGrace = Math.max(0, ragSettings.redGracePeriodDays ?? DEFAULT_RAG_SETTINGS.redGracePeriodDays);

    if (diffDays < -redGrace) {
      return 'red';
    }

    if (diffDays <= amberThreshold) {
      return 'amber';
    }

    return 'green';
  }, [ragSettings]);

  const startTableEdit = (issue: Issue) => {
    setEditingIssueId(issue.id);
    setEditDraft({
      status: issue.status,
      assigneeId: issue.assigneeId || issue.assignee?.id || '',
      priority: issue.priority,
      storyPoints: issue.storyPoints != null ? String(issue.storyPoints) : '',
      dueDate: issue.dueDate ? issue.dueDate.split('T')[0] : ''
    });
  };

  const cancelTableEdit = () => {
    setEditingIssueId(null);
    setEditDraft({});
  };

  const updateDraftField = <K extends keyof typeof editDraft>(field: K, value: (typeof editDraft)[K]) => {
    setEditDraft((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const saveTableEdit = async (issue: Issue): Promise<boolean> => {
    if (!editDraft) return true;
    const payload: Partial<Issue> = {};

    if (editDraft.status && editDraft.status !== issue.status) {
      payload.status = editDraft.status as IssueStatus;
      // preserve columnId when moving between custom columns
      const targetColumn = board?.columns?.find((column: any) => column.status === editDraft.status);
      if (targetColumn) {
        payload.customFields = {
          ...(issue.customFields || {}),
          columnId: targetColumn.id
        };
      }
    }

    if ((editDraft.assigneeId || '') !== (issue.assigneeId || '')) {
      payload.assigneeId = editDraft.assigneeId || undefined;
    }

    if (editDraft.priority && editDraft.priority !== issue.priority) {
      payload.priority = editDraft.priority;
    }

    if (editDraft.storyPoints !== undefined) {
      const parsedStoryPoints = editDraft.storyPoints === '' ? null : Number(editDraft.storyPoints);
      if (parsedStoryPoints !== issue.storyPoints) {
        payload.storyPoints = parsedStoryPoints ?? undefined;
      }
    }

    if (editDraft.dueDate !== undefined) {
      const existingDueDate = issue.dueDate ? issue.dueDate.split('T')[0] : '';
      if ((editDraft.dueDate || '') !== existingDueDate) {
        payload.dueDate = editDraft.dueDate ? new Date(editDraft.dueDate).toISOString() : undefined;
      }
    }

    if (Object.keys(payload).length === 0) {
      cancelTableEdit();
      return true;
    }

    try {
      setSavingIssueId(issue.id);
      await apiService.updateIssue(issue.id, payload);
      await loadBoardData();
      setSnackbar({
        open: true,
        message: 'Issue updated successfully',
        severity: 'success'
      });
      cancelTableEdit();
      return true;
    } catch (error) {
      console.error('Failed to update issue from table view:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update issue. Please try again.',
        severity: 'error'
      });
      return false;
    } finally {
      setSavingIssueId(null);
    }
  };

  const loadIssueComments = async (issueId: string) => {
    try {
      setCommentsLoading(true);
      const comments = await apiService.getIssueComments(issueId);
      setIssueComments(comments);

      if (comments && comments.length > 0) {
        const last = comments[comments.length - 1];
        setCommentsCache((prev) => ({
          ...prev,
          [issueId]: {
            lastComment: last.content || '',
            lastUpdated: last.updatedAt || last.createdAt || ''
          }
        }));
      } else {
        setCommentsCache((prev) => ({
          ...prev,
          [issueId]: {
            lastComment: '',
            lastUpdated: ''
          }
        }));
      }
    } catch (error) {
      console.error('Failed to load comments for issue:', issueId, error);
      setSnackbar({
        open: true,
        message: 'Failed to load comments',
        severity: 'error'
      });
      setIssueComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleOpenComments = async (issue: Issue, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCommentsDialogIssue(issue);
    setCommentsDialogOpen(true);
    await loadIssueComments(issue.id);
  };

  const handleAddComment = async () => {
    if (!commentsDialogIssue || !newComment.trim()) return;
    try {
      setCommentsLoading(true);
      const createdComment = await apiService.addComment(commentsDialogIssue.id, newComment.trim(), false);

      setIssueComments(prev => [...prev, createdComment]);
      setCommentsCache(prev => ({
        ...prev,
        [commentsDialogIssue.id]: {
          lastComment: createdComment.content || '',
          lastUpdated: createdComment.updatedAt || createdComment.createdAt || ''
        }
      }));
      setIssues(prev =>
        prev.map(issue =>
          issue.id === commentsDialogIssue.id
            ? {
                ...issue,
                comments: [...(issue.comments ?? []), createdComment]
              }
            : issue
        )
      );
      setCommentsDialogIssue(prev =>
        prev
          ? {
              ...prev,
              comments: [...(prev.comments ?? []), createdComment]
            }
          : prev
      );

      setNewComment('');
      setSnackbar({
        open: true,
        message: 'Comment added successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to add comment:', error);
      setSnackbar({
        open: true,
        message: 'Failed to add comment',
        severity: 'error'
      });
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleSaveRagSettings = async () => {
    if (!board?.id) {
      return;
    }
    try {
      setSavingRagSettings(true);
      const payload = {
        amberThresholdDays: Math.max(
          0,
          Number(ragSettingsForm.amberThresholdDays || DEFAULT_RAG_SETTINGS.amberThresholdDays)
        ),
        redGracePeriodDays: Math.max(
          0,
          Number(ragSettingsForm.redGracePeriodDays || DEFAULT_RAG_SETTINGS.redGracePeriodDays)
        ),
        enabled: ragSettingsForm.enabled
      };
      await apiService.updateBoardRagSettings(board.id, payload);
      await loadBoardData();
      setSnackbar({
        open: true,
        message: 'RAG settings updated successfully',
        severity: 'success'
      });
      setRagSettingsDialogOpen(false);
    } catch (error) {
      console.error('Failed to update RAG settings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update RAG settings',
        severity: 'error'
      });
    } finally {
      setSavingRagSettings(false);
    }
  };

  const saveCustomColumns = (columns: {[key: string]: {title: string, color: string, icon: any}}) => {
    localStorage.setItem(`board-columns-${projectId}`, JSON.stringify(columns));
  };

  const handleAddColumn = async () => {
    if (!newColumnName.trim() || !board?.id) return;
    
    try {
      // Let backend auto-generate status from name
      const newColumn = await apiService.addColumn(board.id, {
        name: newColumnName,
        // status will be auto-generated by backend
        wipLimit: undefined,
        color: '#1976d2'
      });
      
      // Update the board state with the new column
      setBoard((prevBoard: any) => ({
        ...prevBoard,
        columns: [...(prevBoard.columns || []), newColumn]
      }));
      
      setSnackbar({
        open: true,
        message: `Column "${newColumnName}" added successfully`,
        severity: 'success'
      });
      
      setNewColumnName('');
      setShowAddColumn(false);
    } catch (error) {
      console.error('Error adding column:', error);
      setSnackbar({
        open: true,
        message: 'Failed to add column',
        severity: 'error'
      });
    }
  };

  const handleEditColumn = async (status: string) => {
    if (!editColumnName.trim()) return;
    
    try {
      console.log('Editing column for status:', status);
      console.log('Board data:', board);
      console.log('Board columns:', board?.columns);
      
      // Convert status from kebab-case to lowercase with underscores
      const statusEnum = status.replace(/-/g, '_');
      
      // Find the column ID for this status
      const column = board?.columns?.find((col: any) => col.status === statusEnum);
      console.log('Found column:', column);
      
      if (column) {
        console.log('Updating column via API:', column.id, editColumnName);
        
        // Optimistic update - immediately update the UI
        setBoard((prevBoard: any) => ({
          ...prevBoard,
          columns: prevBoard.columns.map((col: any) => 
            col.id === column.id ? { ...col, name: editColumnName } : col
          )
        }));
        
        // Update via API in background
        console.log('🔄 About to call API to update column...');
        await apiService.updateColumn(column.id, { name: editColumnName });
        console.log('✅ API call completed successfully');
        
        console.log('✅ Column renamed successfully, forcing page reload...');
        
        setSnackbar({
          open: true,
          message: `Column renamed to "${editColumnName}" - Reloading...`,
          severity: 'success'
        });
        
        // Force page reload after a short delay
        setTimeout(() => {
          console.log('🔄 Reloading page now...');
          window.location.reload();
        }, 500);
      } else {
        console.log('Column not found, using fallback');
        // Fallback to local state for custom columns
        if (customColumns[status]) {
          const updatedColumns = {
            ...customColumns,
            [status]: {
              ...customColumns[status],
              title: editColumnName
            }
          };
          setCustomColumns(updatedColumns);
          saveCustomColumns(updatedColumns);
        }
        
        setSnackbar({
          open: true,
          message: `Column renamed to "${editColumnName}"`,
          severity: 'success'
        });
      }
      
      setEditColumnName('');
      setEditingColumn(null);
    } catch (error) {
      console.error('❌ Error in handleEditColumn:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update column',
        severity: 'error'
      });
    }
  };

  const handleDeleteColumn = async (status: string) => {
    try {
      console.log('🔍 Debug: handleDeleteColumn called with status:', status);
      console.log('🔍 Debug: Available statusConfig keys:', Object.keys(statusConfig));
      console.log('🔍 Debug: statusConfig for this status:', statusConfig[status]);
      
      // Find the column to delete using statusConfig (same logic as drop handler)
      const columnId = statusConfig[status]?.columnId;
      
      if (!columnId) {
        console.error('Column ID not found for status:', status);
        console.error('Available statusConfig:', statusConfig);
        setSnackbar({
          open: true,
          message: 'Column not found',
          severity: 'error'
        });
        return;
      }

      // Find the column to delete from board columns using columnId
      const columnToDelete = board?.columns?.find((col: any) => col.id === columnId);

      if (!columnToDelete) {
        console.error('Column not found for columnId:', columnId);
        console.error('Available columns:', board?.columns?.map((col: any) => ({ id: col.id, name: col.name, status: col.status })));
        setSnackbar({
          open: true,
          message: 'Column not found',
          severity: 'error'
        });
        return;
      }

      // Prevent deleting Sprint Backlog column
      if (columnToDelete.name === 'Sprint Backlog' || columnToDelete.status === 'backlog') {
        setSnackbar({
          open: true,
          message: 'Cannot delete Sprint Backlog column',
          severity: 'warning'
        });
        return;
      }

      // Delete the column (backend will handle moving issues to Sprint Backlog)
      await apiService.deleteColumn(columnToDelete.id);

      // Reload board data to reflect changes
      await loadBoardData();

      setSnackbar({
        open: true,
        message: `Column "${columnToDelete.name}" deleted successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting column:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete column',
        severity: 'error'
      });
    }
  };

  const handleAddStory = () => {
    if (!projectId) {
      setSnackbar({
        open: true,
        message: 'Project ID is required.',
        severity: 'warning'
      });
      return;
    }
    
    // Only require sprint for sprint-based boards (sprint, scrum)
    // Kanban and BAU boards don't need a sprint
    if (board?.type === 'sprint' || board?.type === 'scrum') {
      if (!currentSprint) {
        setSnackbar({
          open: true,
          message: 'No active sprint found. Please start a sprint to add stories to it.',
          severity: 'warning'
        });
        return;
      }
    }
    
    setShowAddStoryModal(true);
  };

  const handleCreated = async () => {
    await loadBoardData();
    setShowAddStoryModal(false);
  };

  const handleAddMember = () => {
    setShowAddMemberModal(true);
  };

  const handleCloseAddMemberModal = () => {
    setShowAddMemberModal(false);
  };

  const handleAddMemberSubmit = async (userId: string) => {
    if (!board?.id) return;
    
    try {
      await apiService.assignMemberToBoard(userId, board.id);
      await loadBoardData(); // Reload to show the new member
      setShowAddMemberModal(false);
      setSnackbar({
        open: true,
        message: 'Member added to board successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding member to board:', error);
      setSnackbar({
        open: true,
        message: 'Failed to add member to board',
        severity: 'error'
      });
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!board?.id) return;
    
    try {
      await apiService.removeMemberFromBoard(userId, board.id);
      await loadBoardData(); // Reload to reflect the change
      setSnackbar({
        open: true,
        message: 'Member removed from board successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error removing member from board:', error);
      setSnackbar({
        open: true,
        message: 'Failed to remove member from board',
        severity: 'error'
      });
    }
  };

  // Calculate active filters count
  const getActiveFiltersCount = (): number => {
    let count = 0;
    if (filters.search && filters.search.trim() !== '') count++;
    if (filters.assignees.length > 0) count++;
    if (filters.priorities.length > 0) count++;
    if (filters.types.length > 0) count++;
    if (filters.statuses.length > 0) count++;
    if (filters.dueDateFrom || filters.dueDateTo) count++;
    if (filters.ragStatuses.length > 0) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();
  const hasActiveFilters = activeFiltersCount > 0;

  console.log('🔍 Current issues state:', issues.length, issues);
  const filteredIssues = issues.filter(issue => {
    console.log('🔍 Filtering issue:', issue.title, 'status:', issue.status);
    const ragStatus = computeRagStatus(issue);
    // Search filter - search across multiple fields including Tyvra ID with prefix handling
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase().trim();
      const searchableFields = [
        issue.title,
        issue.description,
        issue.timeTrackerTaskId,
        issue.issueNumber?.toString(),
        issue.labels?.join(' ')
      ].filter(Boolean); // Remove null/undefined values
      
      let matchesSearch = false;
      
      // Check each field for matches
      for (const field of searchableFields) {
        const fieldLower = field?.toLowerCase();
        if (!fieldLower) continue;
        
        // Direct substring match
        if (fieldLower.includes(searchTerm)) {
          matchesSearch = true;
          break;
        }
        
        // Special handling for Tyvra ID prefix patterns
        if (issue.timeTrackerTaskId && field === issue.timeTrackerTaskId) {
          const tyvraId = issue.timeTrackerTaskId.toLowerCase();
          
          // Handle prefix patterns like "TT-", "TT-1", "TT-12", etc.
          if (searchTerm.includes('-')) {
            // If search term contains hyphen, do prefix matching
            if (tyvraId.startsWith(searchTerm)) {
              matchesSearch = true;
              break;
            }
          } else {
            // If search term doesn't contain hyphen, check if it's a prefix
            // e.g., "TT" should match "TT-123", "TT-456"
            const tyvraPrefix = tyvraId.split('-')[0];
            if (tyvraPrefix === searchTerm) {
              matchesSearch = true;
              break;
            }
          }
        }
        
        // Special handling for issue number
        if (issue.issueNumber && field === issue.issueNumber.toString()) {
          const issueNum = issue.issueNumber.toString();
          // Allow partial number matching
          if (issueNum.includes(searchTerm)) {
            matchesSearch = true;
            break;
          }
        }
      }
      
      if (!matchesSearch) {
        return false;
      }
    }

    // Assignee filter
    if (filters.assignees.length > 0) {
      // Check if filtering for unassigned issues
      if (filters.assignees.includes('unassigned')) {
        const hasAnyAssignee = 
          issue.assigneeId || 
          (issue.assignments && issue.assignments.length > 0);
        if (hasAnyAssignee) {
          return false;
        }
      } else {
        // Regular assignee filtering
        const hasMatchingAssignee =
          (issue.assigneeId && filters.assignees.includes(issue.assigneeId)) ||
          (issue.assignments && issue.assignments.some(assignment =>
            assignment.user && assignment.isActive && filters.assignees.includes(assignment.user.id)
          ));
        if (!hasMatchingAssignee) {
          return false;
        }
      }
    }

    // Priority filter
    if (filters.priorities.length > 0 && !filters.priorities.includes(issue.priority)) {
      return false;
    }

    // Type filter
    if (filters.types.length > 0 && !filters.types.includes(issue.type)) {
      return false;
    }

    // Status filter
    if (filters.statuses.length > 0 && !filters.statuses.includes(issue.status)) {
      return false;
    }

    // Due date range filter
    if (filters.dueDateFrom || filters.dueDateTo) {
      if (!issue.dueDate) {
        return false; // Exclude issues without due date if date filter is active
      }
      const issueDueDate = new Date(issue.dueDate);
      issueDueDate.setHours(0, 0, 0, 0); // Reset time to start of day
      
      if (filters.dueDateFrom) {
        const fromDate = new Date(filters.dueDateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (issueDueDate < fromDate) {
          return false;
        }
      }
      
      if (filters.dueDateTo) {
        const toDate = new Date(filters.dueDateTo);
        toDate.setHours(23, 59, 59, 999); // End of day
        if (issueDueDate > toDate) {
          return false;
        }
      }
    }

    if (filters.ragStatuses.length > 0 && !filters.ragStatuses.includes(ragStatus)) {
      return false;
    }

    return true;
  });

  const tableColumns = React.useMemo(
    () =>
      [
        { id: 'issueKey' as TableSortField, label: 'Key' },
        { id: 'title' as TableSortField, label: 'Title' },
        { id: 'type' as TableSortField, label: 'Type' },
        { id: 'status' as TableSortField, label: 'Status' },
        { id: 'dueDate' as TableSortField, label: 'Due Date' },
        { id: 'assignee' as TableSortField, label: 'Assignee(s)' },
        { id: 'ragStatus' as TableSortField, label: 'RAG' },
        { id: 'priority' as TableSortField, label: 'Priority' },
        { id: 'storyPoints' as TableSortField, label: 'Story Points', numeric: true },
        { id: 'lastComment' as TableSortField, label: 'Comments' }
      ],
    []
  );

  const tableColumnWidths = React.useMemo<Record<TableSortField, number | string>>(
    () => ({
      issueKey: 110,
      title: '24%',
      type: 120,
      status: 150,
      dueDate: 140,
      assignee: '14%',
      ragStatus: 120,
      priority: 120,
      storyPoints: 100,
      lastComment: 260
    }),
    []
  );

  const getColumnWidth = React.useCallback(
    (columnId: TableSortField) => {
      const widthValue = tableColumnWidths[columnId];
      if (!widthValue) return undefined;
      return typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
    },
    [tableColumnWidths]
  );

  const groupedIssues = React.useMemo(() => {
    const grouped: { [key: string]: Issue[] } = {};
    
    // Use board columns if available, otherwise fall back to default statuses
    if (board?.columns && board.columns.length > 0) {
      // Sort columns by orderIndex
      const sortedColumns = [...board.columns].sort((a: any, b: any) => a.orderIndex - b.orderIndex);
      
      sortedColumns.forEach((column: any) => {
        // Always use column.status as the key for consistency
        const standardStatuses = ['backlog', 'todo', 'in_progress', 'in_review', 'testing', 'done', 'closed', 'cancelled'];
        
        if (!standardStatuses.includes(column.status)) {
          // Custom column - filter by both status and columnId
          const customIssues = filteredIssues.filter(issue => 
            issue.status === column.status && 
            issue.customFields?.columnId === column.id
          );
          grouped[column.status] = customIssues;
        } else {
          // Standard column - filter by status only
          const standardIssues = filteredIssues.filter(issue => issue.status === column.status);
          grouped[column.status] = standardIssues;
        }
      });
    } else {
      // Fallback to default statuses
      grouped.todo = filteredIssues.filter(issue => issue.status === IssueStatus.TODO);
      grouped['in-progress'] = filteredIssues.filter(issue => issue.status === IssueStatus.IN_PROGRESS);
      grouped.review = filteredIssues.filter(issue => issue.status === IssueStatus.IN_REVIEW);
      grouped.done = filteredIssues.filter(issue => issue.status === IssueStatus.DONE);
    }
    
    console.log('🔍 Final groupedIssues:', grouped);
    return grouped;
  }, [filteredIssues, board?.columns, customColumns]);

  const statusConfig = React.useMemo(() => {
    const config: { [key: string]: { title: string; color: string; icon: any; columnId?: string } } = {};
    
    // Use board columns if available
    if (board?.columns && board.columns.length > 0) {
      console.log('🔍 Building statusConfig from board columns:', board.columns);
      board.columns.forEach((column: any) => {
        // Always use column.status as the key for consistency
        const standardStatuses = ['backlog', 'todo', 'in_progress', 'in_review', 'testing', 'done', 'closed', 'cancelled'];
        
        config[column.status] = {
          title: column.name,
          color: column.color || 'default',
          icon: <Assignment />,
          columnId: column.id
        };
        console.log(`🔍 Added to statusConfig: ${column.status} -> ${column.name} (ID: ${column.id})`);
      });
    } else {
      // Fallback to default statuses
      config.todo = { title: 'To Do', color: 'default', icon: <Assignment /> };
      config['in-progress'] = { title: 'In Progress', color: 'warning', icon: <Assignment /> };
      config.review = { title: 'Review', color: 'info', icon: <Visibility /> };
      config.done = { title: 'Done', color: 'success', icon: <CheckCircle /> };
    }
    
    // Add custom columns
    Object.entries(customColumns).forEach(([key, value]) => {
      config[key] = value;
    });
    
    console.log('🔍 Final statusConfig:', config);
    return config;
  }, [board?.columns, customColumns]);

  const availableStatuses = React.useMemo<{ value: string; label: string }[]>(() => {
    if (board?.columns && board.columns.length > 0) {
      return board.columns.map((column: any) => ({
        value: column.status,
        label: column.name
      }));
    }
    return [
      { value: IssueStatus.BACKLOG, label: 'Backlog' },
      { value: IssueStatus.TODO, label: 'To Do' },
      { value: IssueStatus.IN_PROGRESS, label: 'In Progress' },
      { value: IssueStatus.IN_REVIEW, label: 'In Review' },
      { value: IssueStatus.TESTING, label: 'Testing' },
      { value: IssueStatus.DONE, label: 'Done' },
      { value: IssueStatus.CLOSED, label: 'Closed' },
      { value: IssueStatus.CANCELLED, label: 'Cancelled' }
    ];
  }, [board?.columns]);

  const sortedTableIssues = React.useMemo(() => {
    const { field, direction } = tableSort;
    const multiplier = direction === 'asc' ? 1 : -1;

    const normalizeString = (value: string | undefined | null) =>
      (value || '').toString().toLowerCase();

    const getNumericValue = (value: number) => {
      if (Number.isNaN(value)) {
        return direction === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
      }
      return value;
    };

    const getValue = (issue: Issue): string | number => {
      switch (field) {
        case 'issueKey':
          return normalizeString(getIssueKeyDisplay(issue));
        case 'title':
          return normalizeString(issue.title);
        case 'type':
          return normalizeString(issue.type);
        case 'status':
          return normalizeString(statusConfig[issue.status]?.title || issue.status);
        case 'assignee':
          return normalizeString(getIssueAssigneeNames(issue));
        case 'priority':
          return normalizeString(issue.priority);
        case 'ragStatus': {
          const status = computeRagStatus(issue);
          return RAG_SORT_ORDER[status];
        }
        case 'storyPoints':
          return issue.storyPoints ?? Number.NaN;
        case 'dueDate':
          if (!issue.dueDate) {
            return Number.NaN;
          }
          const date = new Date(issue.dueDate);
          return Number.isNaN(date.getTime()) ? Number.NaN : date.getTime();
        case 'lastComment': {
          const cached = commentsCache[issue.id];
          if (cached) {
            return normalizeString(`${cached.lastUpdated} ${cached.lastComment}`);
          }
          const lastComment = issue.comments && issue.comments.length > 0
            ? issue.comments[issue.comments.length - 1]
            : undefined;
          if (lastComment) {
            return normalizeString(`${lastComment.updatedAt || lastComment.createdAt} ${lastComment.content || ''}`);
          }
          return '';
        }
        default:
          return '';
      }
    };

    const issuesToSort = [...filteredIssues];
    issuesToSort.sort((a, b) => {
      const valueA = getValue(a);
      const valueB = getValue(b);

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        const normalizedA = getNumericValue(valueA);
        const normalizedB = getNumericValue(valueB);
        if (normalizedA === normalizedB) {
          return 0;
        }
        return normalizedA > normalizedB ? multiplier : -multiplier;
      }

      const stringA = normalizeString(valueA as string);
      const stringB = normalizeString(valueB as string);
      return stringA.localeCompare(stringB) * multiplier;
    });

    return issuesToSort;
  }, [filteredIssues, tableSort, statusConfig, commentsCache, computeRagStatus]);

  const isIssueOverdue = React.useCallback((issue: Issue) => {
    if (!issue.dueDate) return false;

    const dueDate = new Date(issue.dueDate);
    if (Number.isNaN(dueDate.getTime())) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    const resolvedStatuses: string[] = [
      IssueStatus.DONE,
      IssueStatus.CLOSED,
      IssueStatus.CANCELLED
    ];

    if (resolvedStatuses.includes(issue.status)) {
      return false;
    }

    return dueDate < today;
  }, []);

  if (loading) {
    return (
      <Layout maxWidth={false} disableGutters={false}>
        <Box sx={{ minHeight: 'calc(100vh - 64px)', position: 'relative', width: '100%' }}>
          <Box sx={{ px: 3, py: 2 }}>
        {/* Header Skeleton */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 32, height: 32, bgcolor: 'grey.300', borderRadius: 1 }} />
              <Box>
                <Box sx={{ width: 200, height: 32, bgcolor: 'grey.300', borderRadius: 1, mb: 1 }} />
                <Box sx={{ width: 300, height: 24, bgcolor: 'grey.200', borderRadius: 1 }} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ width: 100, height: 36, bgcolor: 'grey.300', borderRadius: 2 }} />
              <Box sx={{ width: 120, height: 36, bgcolor: 'grey.300', borderRadius: 2 }} />
            </Box>
          </Box>
        </Box>

        {/* Board Columns Skeleton */}
        <Box sx={{ display: 'flex', gap: 3, overflowX: 'auto', pb: 2 }}>
          {['todo', 'in-progress', 'review', 'done'].map((status) => (
            <Box
              key={status}
              sx={{
                minWidth: 320,
                maxWidth: 320,
                flex: 1
              }}
            >
              <Paper
                sx={{
                  p: 2,
                  height: 600,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.6)})`,
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  borderRadius: 3,
                }}
              >
                {/* Column Header Skeleton */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 20, height: 20, bgcolor: 'grey.300', borderRadius: 1 }} />
                    <Box sx={{ width: 80, height: 24, bgcolor: 'grey.300', borderRadius: 1 }} />
                  </Box>
                  <Box sx={{ width: 30, height: 24, bgcolor: 'grey.300', borderRadius: 1 }} />
                </Box>

                {/* Issues Skeleton */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[1, 2, 3].map((index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        height: 120,
                        bgcolor: 'grey.100',
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box sx={{ width: 60, height: 20, bgcolor: 'grey.300', borderRadius: 1 }} />
                        <Box sx={{ width: 40, height: 20, bgcolor: 'grey.300', borderRadius: 1 }} />
                      </Box>
                      <Box sx={{ width: '100%', height: 16, bgcolor: 'grey.300', borderRadius: 1, mb: 1 }} />
                      <Box sx={{ width: '70%', height: 14, bgcolor: 'grey.200', borderRadius: 1 }} />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Box>
          ))}
        </Box>
        </Box>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout maxWidth={false} disableGutters={false}>
      <Box sx={{ 
        minHeight: 'calc(100vh - 64px)',
        width: '100%'
      }}>
      {/* Modern Professional Header */}
      <Box sx={{ 
        p: 3,
        background: theme.palette.mode === 'dark' 
          ? 'rgba(15, 23, 42, 0.95)' 
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        boxShadow: theme.palette.mode === 'dark' 
          ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
          : '0 4px 20px rgba(0, 0, 0, 0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ViewKanban sx={{ fontSize: 32, color: theme.palette.primary.main }} />
            <Box>
              <Typography variant="h3" sx={{ 
                fontWeight: 700,
                color: theme.palette.text.primary
              }}>
                {board?.name || 'Board'}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                {board?.name ? `${board.name} - Sprint Board` : 'Manage your project tasks and track progress'}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Timeline Button - Show for all boards */}
            {board && (
              <Tooltip title={board.type === 'sprint' ? "View Sprint Timeline" : "View Board Timeline"}>
                <Button
                  variant="outlined"
                  startIcon={<TimelineIcon />}
                  onClick={async () => {
                    try {
                      if (board.type === 'sprint') {
                        // Sprint board: get sprint details
                        let sprintToUse = currentSprint;
                        
                        // If no currentSprint, try to get it from the board
                        if (!sprintToUse || !sprintToUse.id) {
                          if (board?.id) {
                            const boardSprints = await apiService.getBoardSprints(board.id);
                            if (boardSprints && boardSprints.length > 0) {
                              sprintToUse = boardSprints[0];
                            }
                          }
                          
                          // Fallback: get active sprint from project
                          if (!sprintToUse && projectId) {
                            const allSprints = await apiService.getSprints(projectId);
                            sprintToUse = allSprints.find((s: any) => s.isActive === true) ||
                                         allSprints.find((s: any) => s.state === 'ACTIVE') ||
                                         allSprints.find((s: any) => s.state === 'active') ||
                                         (allSprints.length > 0 ? allSprints[0] : null);
                          }
                        }
                        
                      if (sprintToUse && sprintToUse.id) {
                        const details = await apiService.getSprint(sprintToUse.id);
                        console.log('🔍 Sprint details fetched for timeline:', details);
                        setSprintDetailsForTimeline(details);
                        setTimelineDialogOpen(true);
                      } else {
                          setSnackbar({
                            open: true,
                            message: 'No sprint found for this board. Please link a sprint first.',
                            severity: 'warning'
                          });
                        }
                      } else {
                        // Kanban/other boards: show timeline with board issues
                        setSprintDetailsForTimeline(null);
                        setTimelineDialogOpen(true);
                      }
                    } catch (error) {
                      console.error('Error loading timeline:', error);
                      setSnackbar({
                        open: true,
                        message: 'Failed to load timeline',
                        severity: 'error'
                      });
                    }
                  }}
                  sx={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      bgcolor: alpha(theme.palette.primary.main, 0.1)
                    }
                  }}
                >
                  Timeline
                </Button>
              </Tooltip>
            )}
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={async () => {
                try {
                  if (!board?.id) return;
                  const blob = await apiService.exportBoardIssues(board.id);
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${board.name || 'board'}-issues.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (e) {
                  setSnackbar({ open: true, message: 'Failed to export board issues', severity: 'error' });
                }
              }}
              sx={{ borderRadius: 2 }}
            >
              Export CSV
            </Button>
            <Badge 
              badgeContent={activeFiltersCount} 
              color="error"
              invisible={!hasActiveFilters}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.75rem',
                  height: '20px',
                  minWidth: '20px',
                  padding: '0 6px'
                }
              }}
            >
              <Button
                variant={hasActiveFilters ? "contained" : "outlined"}
                color={hasActiveFilters ? "warning" : "inherit"}
                startIcon={<FilterAlt />}
                onClick={() => setFilterDialogOpen(true)}
                sx={{ borderRadius: 2 }}
              >
                Filters
              </Button>
            </Badge>
          <Button
            variant="outlined"
            startIcon={<Traffic />}
            onClick={() => setRagSettingsDialogOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            RAG Settings
          </Button>
            <Button
              variant="outlined"
              startIcon={<Group />}
              onClick={handleAddMember}
              sx={{ borderRadius: 2 }}
            >
              Manage Members
            </Button>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddStory}
              sx={{ borderRadius: 2 }}
            >
              Add Story
            </Button>
          </Box>
        </Box>

        {/* Active Filters Indicator */}
        {hasActiveFilters && (
          <Alert 
            severity="info" 
            icon={<FilterAlt />}
            action={
              <IconButton
                aria-label="clear all filters"
                size="small"
                onClick={() => {
                  setFilters({
                    assignees: [],
                    priorities: [],
                    types: [],
                    statuses: [],
                    search: '',
                    dueDateFrom: '',
                  dueDateTo: '',
                  ragStatuses: []
                  });
                }}
              >
                <Clear fontSize="inherit" />
              </IconButton>
            }
            sx={{ 
              mb: 2,
              borderRadius: 2,
              '& .MuiAlert-message': {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap'
              }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
              Active Filters ({activeFiltersCount}):
            </Typography>
            {filters.search && filters.search.trim() !== '' && (
              <Chip 
                label={`Search: "${filters.search}"`} 
                size="small" 
                onDelete={() => setFilters(prev => ({ ...prev, search: '' }))}
                sx={{ mr: 0.5 }}
              />
            )}
            {filters.assignees.length > 0 && (
              <Chip 
                label={`${filters.assignees.length} Assignee${filters.assignees.length > 1 ? 's' : ''}`} 
                size="small" 
                onDelete={() => setFilters(prev => ({ ...prev, assignees: [] }))}
                sx={{ mr: 0.5 }}
              />
            )}
            {filters.priorities.length > 0 && (
              <Chip 
                label={`${filters.priorities.length} Priorit${filters.priorities.length > 1 ? 'ies' : 'y'}`} 
                size="small" 
                onDelete={() => setFilters(prev => ({ ...prev, priorities: [] }))}
                sx={{ mr: 0.5 }}
              />
            )}
            {filters.types.length > 0 && (
              <Chip 
                label={`${filters.types.length} Type${filters.types.length > 1 ? 's' : ''}`} 
                size="small" 
                onDelete={() => setFilters(prev => ({ ...prev, types: [] }))}
                sx={{ mr: 0.5 }}
              />
            )}
            {filters.statuses.length > 0 && (
              <Chip 
                label={`${filters.statuses.length} Status${filters.statuses.length > 1 ? 'es' : ''}`} 
                size="small" 
                onDelete={() => setFilters(prev => ({ ...prev, statuses: [] }))}
                sx={{ mr: 0.5 }}
              />
            )}
            {(filters.dueDateFrom || filters.dueDateTo) && (
              <Chip 
                label={`Due Date Range`} 
                size="small" 
                onDelete={() => setFilters(prev => ({ ...prev, dueDateFrom: '', dueDateTo: '' }))}
                sx={{ mr: 0.5 }}
              />
            )}
            {filters.ragStatuses.length > 0 && (
              <Chip
                label={`RAG: ${filters.ragStatuses.map(status => status.toUpperCase()).join(', ')}`}
                size="small"
                onDelete={handleClearRagStatuses}
                sx={{ mr: 0.5 }}
              />
            )}
            <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
              Showing {filteredIssues.length} of {issues.length} issues
            </Typography>
          </Alert>
        )}

        {/* Search and Quick Stats */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1, md: 1.5 },
            mb: 2
          }}
        >
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={{ xs: 1, md: 1.5 }}
            alignItems={{ xs: 'stretch', lg: 'center' }}
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <TextField
              placeholder="Search issues..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{
                flex: { xs: 1, lg: '0 0 320px' },
                minWidth: { xs: '100%', sm: 260 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: { xs: 'flex-start', lg: 'flex-end' }
              }}
            >
              <Chip
                label={`${sortedTableIssues.length} shown`}
                color="primary"
                variant="outlined"
                size="small"
              />
              {filteredIssues.length !== issues.length && (
                <Chip
                  label={`${filteredIssues.length} matching filters`}
                  color="info"
                  variant="outlined"
                  size="small"
                />
              )}
              <Chip
                label={`${issues.length} total`}
                color="default"
                variant="outlined"
                size="small"
              />
            </Box>
          </Stack>

        </Box>
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: { xs: 1, md: 1.5 },
            justifyContent: 'space-between'
          }}
        >
          <Tabs
            value={viewMode}
            onChange={handleViewModeChange}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 140
              }
            }}
          >
            <Tab value="board" label="Board View" />
            <Tab value="table" label="Table View" />
          </Tabs>

          <Box
            sx={{
              mt: { xs: 1, md: 0 },
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
              rowGap: 1,
              justifyContent: { xs: 'flex-start', lg: 'flex-end' },
              flex: 1,
              overflowX: 'auto',
              pb: 0.5,
              '&::-webkit-scrollbar': {
                height: 6
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: alpha(theme.palette.primary.main, 0.3),
                borderRadius: 3
              }
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, flexShrink: 0 }}>
              Type:
            </Typography>
            {(() => {
              const selectedType = filters.types.length === 0 ? 'all' : (filters.types[0] || 'all');
              const types = [
                { value: 'all', label: 'All', color: 'default' as const },
                { value: 'story', label: getIssueTypeLabel('story'), color: 'primary' as const },
                { value: 'task', label: getIssueTypeLabel('task'), color: 'info' as const },
                { value: 'subtask', label: getIssueTypeLabel('subtask'), color: 'secondary' as const },
                { value: 'bug', label: getIssueTypeLabel('bug'), color: 'error' as const },
              ];
              return types.map(t => (
                <Chip
                  key={t.value}
                  label={t.label}
                  color={t.color as any}
                  variant={selectedType === t.value ? 'filled' : 'outlined'}
                  size="small"
                  onClick={() => {
                    if (t.value === 'all') {
                      setFilters(prev => ({ ...prev, types: [] }));
                    } else {
                      setFilters(prev => ({ ...prev, types: [t.value] }));
                    }
                  }}
                  sx={{
                    height: 26,
                    borderRadius: 2,
                    whiteSpace: 'nowrap',
                    px: 1,
                    flexShrink: 0
                  }}
                />
              ));
            })()}

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: 'none', md: 'block' },
                borderColor: alpha(theme.palette.divider, 0.4)
              }}
            />

            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, flexShrink: 0 }}>
              RAG:
            </Typography>
            <Chip
              label="All"
              variant={filters.ragStatuses.length === 0 ? 'filled' : 'outlined'}
              color={filters.ragStatuses.length === 0 ? 'default' : undefined}
              size="small"
              onClick={handleClearRagStatuses}
              sx={{
                height: 26,
                borderRadius: 2,
                px: 1,
                backgroundColor: filters.ragStatuses.length === 0 ? alpha(theme.palette.text.primary, 0.08) : 'transparent',
                borderColor: alpha(theme.palette.text.primary, 0.2),
                flexShrink: 0
              }}
            />
            {ragStatusOptions.map(option => {
              const isSelected = filters.ragStatuses.includes(option.value);
              const hoverBg = alpha(option.color, 0.15);
              const contrastColor = theme.palette.getContrastText(option.color);
              return (
                <Chip
                  key={option.value}
                  label={option.label}
                  variant={isSelected ? 'filled' : 'outlined'}
                  size="small"
                  onClick={() => handleToggleRagStatus(option.value)}
                  sx={{
                    height: 26,
                    borderRadius: 2,
                    px: 1,
                    borderColor: alpha(option.color, 0.6),
                    backgroundColor: isSelected ? option.color : 'transparent',
                    color: isSelected ? contrastColor : option.color,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: isSelected ? option.color : hoverBg
                    },
                    flexShrink: 0
                  }}
                />
              );
            })}

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: 'none', md: 'block' },
                borderColor: alpha(theme.palette.divider, 0.4)
              }}
            />

            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, flexShrink: 0 }}>
              Assignees:
            </Typography>
            {users.length === 0 && (
              <Typography variant="caption" color="warning.main" sx={{ fontStyle: 'italic', flexShrink: 0 }}>
                No board members found
              </Typography>
            )}
            <Chip
              icon={<Group />}
              label="All"
              variant={filters.assignees.length === 0 ? 'filled' : 'outlined'}
              color="default"
              size="small"
              onClick={() => setFilters(prev => ({ ...prev, assignees: [] }))}
              sx={{
                height: 28,
                borderRadius: 2,
                flexShrink: 0,
                '& .MuiChip-icon': {
                  fontSize: 16
                }
              }}
            />
            <Chip
              icon={<PersonOff />}
              label="Unassigned"
              variant={filters.assignees.includes('unassigned') ? 'filled' : 'outlined'}
              color={filters.assignees.includes('unassigned') ? 'warning' : 'default'}
              size="small"
              onClick={() => {
                if (filters.assignees.includes('unassigned')) {
                  setFilters(prev => ({ 
                    ...prev, 
                    assignees: prev.assignees.filter(id => id !== 'unassigned') 
                  }));
                } else {
                  setFilters(prev => ({ 
                    ...prev, 
                    assignees: ['unassigned'] 
                  }));
                }
              }}
              sx={{
                height: 28,
                borderRadius: 2,
                flexShrink: 0,
                '& .MuiChip-icon': {
                  fontSize: 16
                }
              }}
            />
            {users.map(user => {
              const isSelected = filters.assignees.includes(user.id);
              const issueCount = issues.filter(issue => 
                issue.assigneeId === user.id || 
                (issue.assignments && issue.assignments.some(assignment => assignment.user?.id === user.id && assignment.isActive))
              ).length;
              
              return (
                <Tooltip key={user.id} title={`${user.firstName} ${user.lastName} (${issueCount} issues)`} arrow>
                  <Chip
                    avatar={
                      <Avatar 
                        sx={{ 
                          width: 22, 
                          height: 22, 
                          fontSize: '0.7rem',
                          bgcolor: isSelected ? 'primary.contrastText' : 'primary.main',
                          color: isSelected ? 'primary.main' : 'primary.contrastText'
                        }}
                      >
                        {user.firstName?.[0]}{user.lastName?.[0]}
                      </Avatar>
                    }
                    label={`${user.firstName?.[0]}${user.lastName?.[0]}`}
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    size="small"
                    onClick={() => {
                      if (isSelected) {
                        setFilters(prev => ({ 
                          ...prev, 
                          assignees: prev.assignees.filter(id => id !== user.id) 
                        }));
                      } else {
                        setFilters(prev => ({ 
                          ...prev, 
                          assignees: [...prev.assignees, user.id] 
                        }));
                      }
                    }}
                    sx={{
                      height: 28,
                      borderRadius: 2,
                      flexShrink: 0,
                      '& .MuiChip-avatar': {
                        ml: 0.5
                      },
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: 1
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      </Box>

      {viewMode === 'table' ? (
        <Box sx={{ px: 3, py: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Excel-Style Table View
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Quickly scan, sort, and open issues in a spreadsheet-friendly layout.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`${sortedTableIssues.length} shown`}
                color="primary"
                variant="outlined"
              />
              {filteredIssues.length !== issues.length && (
                <Chip
                  label={`${filteredIssues.length} matching filters`}
                  color="info"
                  variant="outlined"
                />
              )}
              <Chip
                label={`${issues.length} total`}
                color="default"
                variant="outlined"
              />
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              overflow: 'hidden'
            }}
          >
            <TableContainer
              sx={{
                maxHeight: 'calc(100vh - 260px)',
                '&::-webkit-scrollbar': {
                  height: 10,
                  width: 10
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.4),
                  borderRadius: 6
                }
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  tableLayout: 'fixed',
                  width: '100%',
                  minWidth: 1080,
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  '& thead th, & tbody td': {
                    borderRight: `1px solid ${alpha(theme.palette.divider, 0.12)}`
                  },
                  '& thead th:last-of-type, & tbody td:last-of-type': {
                    borderRight: 'none'
                  }
                }}
              >
                <colgroup>
                  {tableColumns.map((column) => {
                    const widthValue = getColumnWidth(column.id);
                    return <col key={column.id} style={widthValue ? { width: widthValue } : undefined} />;
                  })}
                  <col style={{ width: '80px' }} />
                </colgroup>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      '& > th': {
                        position: 'sticky',
                        top: 0,
                        zIndex: 3,
                        py: 1.5,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                        backgroundColor: theme.palette.background.paper
                      }
                    }}
                  >
                    {tableColumns.map((column) => {
                      const widthValue = getColumnWidth(column.id);
                      const isTextColumn = column.id === 'title' || column.id === 'lastComment';
                      const headerAlign =
                        column.id === 'storyPoints'
                          ? 'center'
                          : isTextColumn
                              ? 'left'
                              : 'center';
                      return (
                        <TableCell
                          key={column.id}
                          sortDirection={tableSort.field === column.id ? tableSort.direction : false}
                          align={headerAlign}
                          sx={{
                            ...(widthValue
                              ? {
                                  width: widthValue,
                                  maxWidth: widthValue
                                }
                              : {}),
                            fontWeight: 700,
                            backgroundColor: theme.palette.background.paper,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          <TableSortLabel
                            active={tableSort.field === column.id}
                            direction={tableSort.field === column.id ? tableSort.direction : 'asc'}
                            onClick={() => handleTableSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        </TableCell>
                      );
                    })}
                    <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: theme.palette.background.paper, minWidth: 120 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedTableIssues.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length}>
                        <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            No issues to display
                          </Typography>
                          <Typography variant="body2">
                            Adjust filters or add a new issue to see it here.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedTableIssues.map((issue) => {
                      const overdue = issue.isOverdue || isIssueOverdue(issue);
                      const bodyCellBase = {
                        py: 1.75,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`
                      };
                      const commonCellSx = {
                        whiteSpace: 'nowrap' as const,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      };
                      const commentInfo = commentsCache[issue.id];
                      const lastCommentRaw =
                        commentInfo?.lastComment ||
                        (issue.comments && issue.comments.length > 0
                          ? issue.comments[issue.comments.length - 1].content || ''
                          : '');
                      const lastComment =
                        lastCommentRaw && lastCommentRaw.length > 50
                          ? `${lastCommentRaw.slice(0, 50)}...`
                          : lastCommentRaw;
                      const ragStatus = computeRagStatus(issue);
                      const ragLabel =
                        ragStatus === 'red' ? 'Red' : ragStatus === 'amber' ? 'Amber' : 'Green';
                      const ragColor =
                        ragStatus === 'red'
                          ? theme.palette.error.main
                          : ragStatus === 'amber'
                            ? '#fb8c00'
                            : theme.palette.success.main;
                      const isEditing = editingIssueId === issue.id;
                      const statusEntry = statusConfig[issue.status];
                      const statusLabel = statusEntry?.title || issue.status;
                      const statusColor = statusEntry?.color || 'default';
                      const muiChipColors = ['default', 'primary', 'secondary', 'info', 'success', 'warning', 'error'];
                      const isMuiColor = muiChipColors.includes(statusColor);
                      const issueKey = getIssueKeyDisplay(issue);
                      const assignees = getIssueAssigneeNames(issue);
                      const displayTitle =
                        issue.title && issue.title.length > 50
                          ? `${issue.title.slice(0, 50)}…`
                          : issue.title || '';
                      return (
                        <TableRow
                          key={issue.id}
                          hover
                          onClick={async () => {
                            if (editingIssueId === issue.id || savingIssueId) {
                              return;
                            }

                            if (editingIssueId) {
                              const currentlyEditing =
                                sortedTableIssues.find((item) => item.id === editingIssueId) ||
                                issues.find((item) => item.id === editingIssueId);

                              if (currentlyEditing) {
                                const didSave = await saveTableEdit(currentlyEditing);
                                if (!didSave) {
                                  return;
                                }
                              } else {
                                cancelTableEdit();
                              }
                            }

                            startTableEdit(issue);
                          }}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor:
                              ragStatus === 'red'
                                ? alpha(theme.palette.error.main, 0.08)
                                : ragStatus === 'amber'
                                  ? alpha('#fb8c00', 0.08)
                                  : 'inherit',
                            ...(ragStatus !== 'red' && ragStatus !== 'amber'
                              ? {
                                  '&:nth-of-type(even)': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.025)
                                  }
                                }
                              : {}),
                            '&:hover': {
                              backgroundColor:
                                ragStatus === 'red'
                                  ? alpha(theme.palette.error.main, 0.12)
                                  : ragStatus === 'amber'
                                    ? alpha('#fb8c00', 0.12)
                                    : alpha(theme.palette.primary.main, 0.04)
                            },
                            transition: 'background-color 0.2s ease'
                          }}
                        >
                          <TableCell
                            align="center"
                            sx={{
                              ...commonCellSx,
                              ...bodyCellBase,
                              ...(getColumnWidth('issueKey')
                                ? { width: getColumnWidth('issueKey'), maxWidth: getColumnWidth('issueKey') }
                                : {}),
                              px: 1
                            }}
                          >
                            {isEditing ? (
                              <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center' }}>
                                {issueKey}
                              </Typography>
                            ) : (
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Chip
                                  label={issueKey}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontWeight: 600 }}
                                />
                              </Box>
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...commonCellSx,
                              ...bodyCellBase,
                              ...(getColumnWidth('title')
                                ? { width: getColumnWidth('title'), maxWidth: getColumnWidth('title') }
                                : { maxWidth: 480 }),
                              px: 1.5
                            }}
                          >
                            <Tooltip title={issue.title} arrow>
                              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                {displayTitle}
                              </Typography>
                            </Tooltip>
                            {issue.timeTrackerTaskId && (
                              <Typography variant="caption" color="text.secondary">
                                {issue.timeTrackerTaskId}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              ...bodyCellBase,
                              ...(getColumnWidth('type')
                                ? { width: getColumnWidth('type'), maxWidth: getColumnWidth('type') }
                                : {}),
                              px: 1
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Chip
                                icon={getIssueTypeIcon(issue.type)}
                                label={issue.type}
                                size="small"
                                color={getIssueTypeColor(issue.type) as any}
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              ...commonCellSx,
                              ...bodyCellBase,
                              ...(getColumnWidth('status')
                                ? { width: getColumnWidth('status'), maxWidth: getColumnWidth('status') }
                                : {}),
                              px: 1
                            }}
                          >
                            {isEditing ? (
                              <Select
                                value={editDraft.status || ''}
                                size="small"
                                fullWidth
                                onChange={(e) => updateDraftField('status', e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ '& .MuiSelect-select': { textAlign: 'center' } }}
                              >
                                {availableStatuses.map((statusOption) => (
                                  <MenuItem key={statusOption.value} value={statusOption.value}>
                                    {statusOption.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            ) : (
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Chip
                                  label={statusLabel}
                                  size="small"
                                  color={isMuiColor ? (statusColor as any) : 'default'}
                                  variant={isMuiColor ? 'filled' : 'outlined'}
                                  sx={
                                    !isMuiColor && statusColor
                                      ? {
                                          bgcolor: statusColor,
                                          color: '#fff',
                                          borderColor: statusColor
                                        }
                                      : undefined
                                  }
                                />
                              </Box>
                            )}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              ...commonCellSx,
                              ...bodyCellBase,
                              ...(getColumnWidth('dueDate')
                                ? { width: getColumnWidth('dueDate'), maxWidth: getColumnWidth('dueDate') }
                                : {}),
                              px: 1
                            }}
                          >
                            {isEditing ? (
                              <TextField
                                value={editDraft.dueDate ?? ''}
                                size="small"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) => updateDraftField('dueDate', e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                inputProps={{ style: { textAlign: 'center' } }}
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  color:
                                    ragStatus === 'red'
                                      ? theme.palette.error.main
                                      : ragStatus === 'amber'
                                        ? '#fb8c00'
                                        : 'inherit',
                                  fontWeight: ragStatus === 'red' ? 600 : undefined,
                                  textAlign: 'center'
                                }}
                              >
                                {formatDisplayDate(issue.dueDate)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              ...commonCellSx,
                              ...bodyCellBase,
                              ...(getColumnWidth('assignee')
                                ? { width: getColumnWidth('assignee'), maxWidth: getColumnWidth('assignee') }
                                : {}),
                              px: 1
                            }}
                          >
                            {isEditing ? (
                              <Select
                                value={editDraft.assigneeId || ''}
                                size="small"
                                fullWidth
                                displayEmpty
                                onChange={(e) => updateDraftField('assigneeId', e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ '& .MuiSelect-select': { textAlign: 'center' } }}
                              >
                                <MenuItem value="">
                                  <em>Unassigned</em>
                                </MenuItem>
                                {users.map((member) => (
                                  <MenuItem key={member.id} value={member.id}>
                                    {member.firstName} {member.lastName}
                                  </MenuItem>
                                ))}
                              </Select>
                            ) : (
                              <Typography variant="body2" noWrap sx={{ textAlign: 'center', width: '100%' }}>
                                {assignees}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              ...commonCellSx,
                              ...bodyCellBase,
                              ...(getColumnWidth('ragStatus')
                                ? { width: getColumnWidth('ragStatus'), maxWidth: getColumnWidth('ragStatus') }
                                : {}),
                              px: 1
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Chip
                                label={ragLabel}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  backgroundColor: alpha(ragColor, 0.15),
                                  color: ragColor,
                                  borderColor: alpha(ragColor, 0.4),
                                  borderWidth: 1,
                                  borderStyle: 'solid'
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              ...commonCellSx,
                              ...bodyCellBase,
                              ...(getColumnWidth('priority')
                                ? { width: getColumnWidth('priority'), maxWidth: getColumnWidth('priority') }
                                : {}),
                              px: 1
                            }}
                          >
                            {isEditing ? (
                              <Select
                                value={editDraft.priority || ''}
                                size="small"
                                fullWidth
                                onChange={(e) => updateDraftField('priority', e.target.value as Priority)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ '& .MuiSelect-select': { textAlign: 'center' } }}
                              >
                                {Object.values(Priority).map((priority) => (
                                  <MenuItem key={priority} value={priority}>
                                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                                  </MenuItem>
                                ))}
                              </Select>
                            ) : (
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Chip
                                  label={issue.priority}
                                  size="small"
                                  color={getPriorityColor(issue.priority) as any}
                                  variant="outlined"
                                  sx={{ textTransform: 'capitalize' }}
                                />
                              </Box>
                            )}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              ...commonCellSx,
                              ...bodyCellBase,
                              ...(getColumnWidth('storyPoints')
                                ? { width: getColumnWidth('storyPoints'), maxWidth: getColumnWidth('storyPoints') }
                                : {}),
                              px: 1
                            }}
                          >
                            {isEditing ? (
                              <TextField
                                value={editDraft.storyPoints ?? ''}
                                size="small"
                                type="number"
                                fullWidth
                                inputProps={{ min: 0, style: { textAlign: 'center' } }}
                                onChange={(e) => updateDraftField('storyPoints', e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center' }}>
                                {issue.storyPoints != null ? issue.storyPoints : '—'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{
                              ...commonCellSx,
                              ...bodyCellBase,
                              ...(getColumnWidth('lastComment')
                                ? { width: getColumnWidth('lastComment'), maxWidth: getColumnWidth('lastComment') }
                                : { maxWidth: 340 }),
                              px: 1.25,
                              pl: 2.5
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                              <Tooltip title={lastCommentRaw || 'No comments yet'} arrow>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: lastComment ? 'text.primary' : 'text.secondary'
                                  }}
                                >
                                  {lastComment ? lastComment : 'No comments'}
                                </Typography>
                              </Tooltip>
                              <Tooltip title="View & add comments">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => handleOpenComments(issue, e)}
                                >
                                  <ChatBubbleOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ whiteSpace: 'nowrap', ...bodyCellBase }}>
                            <Tooltip title={isEditing ? 'Save changes' : 'View details'}>
                              <span>
                                <IconButton
                                  size="small"
                                  color={isEditing ? 'primary' : 'default'}
                                  onClick={(e) => handleRowPrimaryAction(issue, e)}
                                  disabled={isEditing && savingIssueId === issue.id}
                                >
                                  {isEditing ? (
                                    savingIssueId === issue.id ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <CheckCircle />
                                    )
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            {isEditing && (
                              <Tooltip title="Cancel">
                                <IconButton
                                  color="inherit"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    cancelTableEdit();
                                  }}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      ) : (
        <>
      {/* Modern Board Content - Jira/Trello Style: Window scrolls horizontally, columns scroll vertically */}
      <Box 
        sx={{ 
          display: 'flex', 
          gap: 3,
          px: 3,
          py: 2,
          // CRITICAL: This is the main board container that scrolls HORIZONTALLY across columns
          position: 'relative',
          height: 'calc(100vh - 200px)', // Fixed height (like Jira/Trello)
          maxHeight: 'calc(100vh - 200px)',
          overflowX: 'auto', // Board scrolls horizontally (left/right) to see more columns
          overflowY: 'hidden', // NO vertical scroll at board level - columns handle their own vertical scroll
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          // Horizontal scrollbar styling (for column navigation)
          '&::-webkit-scrollbar': {
            height: 12,
          },
          '&::-webkit-scrollbar-track': {
            background: alpha(theme.palette.grey[300], 0.15),
            borderRadius: 8,
            margin: '0 16px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.primary.main, 0.4),
            borderRadius: 8,
            border: `2px solid ${theme.palette.background.default}`,
            '&:hover': {
              background: alpha(theme.palette.primary.main, 0.6),
            },
            '&:active': {
              background: alpha(theme.palette.primary.main, 0.8),
            }
          },
        }}
      >
        {Object.entries(groupedIssues).map(([status, statusIssues], index) => (
          <Grow in={true} timeout={200 + index * 100} key={status}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              {/* Drop zone before column */}
              {index > 0 && (
                <Box
                  onDragOver={handleColumnDragOver}
                  onDrop={(e) => handleColumnDrop(e, status)}
                  sx={{
                    width: 16,
                    minWidth: 16,
                    height: '100%',
                    backgroundColor: draggedColumn ? alpha(theme.palette.primary.main, 0.15) : 'transparent',
                    border: draggedColumn ? `2px dashed ${theme.palette.primary.main}` : 'none',
                    borderRadius: 2,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    mx: 0.5,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.25),
                      borderColor: theme.palette.primary.main,
                      transform: 'scaleY(1.1)',
                    }
                  }}
                />
              )}
              
              {/* Modern Column */}
              <Paper
                onDragOver={handleDragOver}
                onDrop={(e) => {
                  console.log('🔍 onDrop called for status:', status);
                  console.log('🔍 statusConfig[status]:', statusConfig[status]);
                  const columnId = statusConfig[status]?.columnId;
                  console.log('🔍 columnId found:', columnId);
                  if (columnId) {
                    handleDrop(e, columnId);
                  } else {
                    console.error('❌ No columnId found for status:', status);
                    console.error('❌ Available statusConfig keys:', Object.keys(statusConfig));
                  }
                }}
                onDragLeave={handleDragLeave}
                elevation={0}
                sx={{
                  minWidth: 340,
                  maxWidth: 340,
                  // CRITICAL: Fixed height for each column (Jira/Trello pattern)
                  height: 'calc(100vh - 220px)', // Match parent container height
                  maxHeight: 'calc(100vh - 220px)',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${alpha(theme.palette.divider, 0.25)}`,
                  borderRadius: 3,
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.25),
                    boxShadow: `0 6px 18px ${alpha(theme.palette.common.black, 0.08)}`,
                  }
                }}
              >
                {/* Modern Column Header */}
                <Box 
                  draggable
                  onDragStart={(e) => handleColumnDragStart(e, status)}
                  onDragEnd={handleColumnDragEnd}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    p: 2,
                    pb: 2,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    cursor: 'grab',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.03),
                      '& .drag-indicator': {
                        opacity: 1,
                        transform: 'scale(1.1)',
                      }
                    },
                    '&:active': {
                      cursor: 'grabbing',
                      transform: 'scale(0.98)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                    <DragIndicator 
                      className="drag-indicator"
                      sx={{ 
                        fontSize: 18, 
                        color: alpha(theme.palette.text.secondary, 0.6),
                        opacity: 0,
                        transition: 'all 0.2s ease',
                        cursor: 'grab',
                      }} 
                    />
                    {editingColumn === status ? (
                      <TextField
                        value={editColumnName}
                        onChange={(e) => setEditColumnName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleEditColumn(status)}
                        onBlur={() => handleEditColumn(status)}
                        autoFocus
                        size="small"
                        variant="outlined"
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            borderRadius: 2,
                          }
                        }}
                      />
                    ) : (
                      <Typography variant="h6" sx={{ 
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        fontSize: '1rem',
                        letterSpacing: '-0.02em'
                      }}>
                        {statusConfig[status]?.title || status}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Badge 
                      badgeContent={statusIssues.length} 
                      color="primary"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          minWidth: 20,
                          height: 20,
                        }
                      }}
                    >
                      <Box sx={{ width: 8, height: 8 }} />
                    </Badge>
                    <Tooltip title="Edit column name">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingColumn(status);
                          const columnName = statusConfig[status]?.title || status;
                          setEditColumnName(columnName);
                        }}
                        sx={{ 
                          opacity: 0.6,
                          transition: 'all 0.2s ease',
                          '&:hover': { 
                            opacity: 1,
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            transform: 'scale(1.1)',
                          }
                        }}
                      >
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete column">
                      <IconButton
                        size="small"
                        onClick={() => {
                          console.log('🚨 DELETE BUTTON CLICKED for status:', status);
                          console.log('🚨 statusConfig:', statusConfig);
                          console.log('🚨 statusConfig[status]:', statusConfig[status]);
                          handleDeleteColumn(status);
                        }}
                        sx={{ 
                          opacity: 0.6,
                          transition: 'all 0.2s ease',
                          '&:hover': { 
                            opacity: 1, 
                            color: 'error.main',
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                            transform: 'scale(1.1)',
                          }
                        }}
                      >
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Modern Issues Container - Fixed scrolling to prevent last item cutoff */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 1.5,
                  px: 1.5, // Horizontal padding
                  pt: 1.5, // Top padding
                  pb: 3, // Extra bottom padding to ensure last item is fully visible
                  flex: '1 1 auto', // Allow flex to shrink and grow
                  minHeight: 0, // CRITICAL: Allow flex child to shrink below content size
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  scrollBehavior: 'smooth', // Smooth vertical scrolling
                  WebkitOverflowScrolling: 'touch', // iOS smooth scrolling
                  // Ensure proper scrolling behavior
                  boxSizing: 'border-box',
                  '&::-webkit-scrollbar': {
                    width: 8, // Slightly wider for better visibility
                  },
                  '&::-webkit-scrollbar-track': {
                    background: alpha(theme.palette.grey[300], 0.1),
                    borderRadius: 4,
                    margin: '4px 0',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: alpha(theme.palette.primary.main, 0.3),
                    borderRadius: 4,
                    transition: 'background 0.2s ease',
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.5),
                    },
                  },
                  '&:empty::after': {
                    content: '"Drop issues here"',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: alpha(theme.palette.text.secondary, 0.4),
                    fontSize: '0.875rem',
                    fontStyle: 'italic',
                    border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                    borderRadius: 2,
                    margin: 1,
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                  }
                }}>
                                     {statusIssues.map((issue, issueIndex) => {
                  const ragStatusForCard = computeRagStatus(issue);
                  const ragLabelForCard =
                    ragStatusForCard === 'red' ? 'Red' : ragStatusForCard === 'amber' ? 'Amber' : 'Green';
                  const ragColorForCard =
                    ragStatusForCard === 'red'
                      ? theme.palette.error.main
                      : ragStatusForCard === 'amber'
                        ? '#fb8c00'
                        : theme.palette.success.main;

                  return (
                    <Slide key={issue.id} direction="up" in={true} timeout={300 + issueIndex * 50}>
                      <Card
                        key={issue.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, issue)}
                        onDragEnd={handleDragEnd}
                        elevation={0}
                        sx={{
                          cursor: 'grab',
                          transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
                          border: `1px solid ${
                            ragStatusForCard === 'red' || ragStatusForCard === 'amber'
                              ? alpha(ragColorForCard, 0.4)
                              : alpha(theme.palette.divider, 0.2)
                          }`,
                          minHeight: 140, // Consistent card height
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: 2,
                          // Fix last card cutoff - add extra bottom margin for last item
                          mb: issueIndex === statusIssues.length - 1 ? 2 : 1,
                          position: 'relative',
                          overflow: 'hidden',
                          '&:hover': {
                            boxShadow: `0 10px 20px ${alpha(theme.palette.common.black, 0.08)}`,
                            borderColor: alpha(theme.palette.primary.main, 0.25),
                            transform: 'translateY(-2px)',
                            '& .issue-actions': {
                              opacity: 1,
                              transform: 'translateY(0)',
                            },
                            '& .issue-priority': {
                              opacity: 1,
                            }
                          },
                          '&:active': {
                            cursor: 'grabbing',
                            transform: 'scale(0.995)',
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 2,
                            background: `linear-gradient(90deg, ${getPriorityColor(issue.priority)}, ${getIssueTypeColor(issue.type)})`,
                            opacity: 0.6,
                          }
                        }}
                        onClick={() => handleIssueClick(issue)}
                    >
                      <CardContent sx={{ p: 2 }}>
                        {/* Issue Header */}
                                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                              <Chip
                                icon={getIssueTypeIcon(issue.type)}
                                label={issue.type}
                                color={getIssueTypeColor(issue.type) as any}
                                size="small"
                                sx={{ 
                                  fontWeight: 500,
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                                onClick={(e) => handleChildOpenIssue(issue, e)}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ 
                                fontWeight: 500,
                                fontSize: '0.7rem'
                              }}>
                                {issue.issueKey || `#${issue.issueNumber}`}
                              </Typography>
                            </Box>
                          
                          <Box 
                            className="issue-actions"
                            sx={{ 
                              opacity: 0,
                              transition: 'opacity 0.2s ease-in-out',
                              display: 'flex',
                              gap: 0.5
                            }}
                          >
                            <Tooltip title="View Details">
                              <IconButton size="small">
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton size="small">
                                <Edit />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        {/* Issue Title - Fixed length with tooltip for full title */}
                        <Tooltip title={issue.title} placement="top" arrow>
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              fontWeight: 600, 
                              mb: 1,
                              lineHeight: 1.4,
                              fontSize: '0.875rem',
                              display: '-webkit-box',
                              WebkitLineClamp: 2, // Limit to 2 lines with ellipsis
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              cursor: 'pointer',
                              '&:hover': {
                                color: theme.palette.primary.main,
                              }
                            }}
                          >
                            {issue.title}
                          </Typography>
                        </Tooltip>

                        {/* Issue Description */}
                        {issue.description && (
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              mb: 2,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {issue.description}
                          </Typography>
                        )}

                        {/* Issue Meta */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                          <Chip
                            label={issue.priority}
                            color={getPriorityColor(issue.priority) as any}
                            size="small"
                            sx={{ height: 20, fontWeight: 500 }}
                            onClick={(e) => handleChildOpenIssue(issue, e)}
                          />
                          {issue.storyPoints && (
                            <Chip
                              label={`${issue.storyPoints} SP`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontWeight: 500 }}
                              onClick={(e) => handleChildOpenIssue(issue, e)}
                            />
                          )}
                          <Chip
                            label={ragLabelForCard}
                            size="small"
                            sx={{
                              height: 20,
                              fontWeight: 500,
                              backgroundColor: alpha(ragColorForCard, 0.15),
                              color: ragColorForCard
                            }}
                          />
                        </Box>

                        {/* Assignees */}
                        {issue.assignments && issue.assignments.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            {issue.assignments.slice(0, 3).map((assignment) => (
                              <Tooltip key={assignment.id} title={assignment.user?.firstName + ' ' + assignment.user?.lastName}>
                                <Avatar
                                  src={assignment.user?.avatar}
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    fontSize: '0.7rem',
                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                    color: theme.palette.primary.main
                                  }}
                                  onClick={(e) => handleChildOpenIssue(issue, e)}
                                >
                                  {assignment.user?.firstName?.[0]}{assignment.user?.lastName?.[0]}
                                </Avatar>
                              </Tooltip>
                            ))}
                            {issue.assignments.length > 3 && (
                              <Typography variant="caption" color="text.secondary">
                                +{issue.assignments.length - 3} more
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Issue Footer */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(issue.updatedAt).toLocaleDateString()}
                          </Typography>
                          {ragStatusForCard === 'red' && (
                            <Chip
                              icon={<Warning />}
                              label="Overdue"
                              color="error"
                              size="small"
                              sx={{ height: 20 }}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Slide>
                  );
                })}
                
                {statusIssues.length === 0 && (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 4, 
                    color: 'text.secondary',
                    border: `2px dashed ${alpha(theme.palette.divider, 0.3)}`,
                    borderRadius: 2
                  }}>
                    <Assignment sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                    <Typography variant="body2">
                      No issues in this column
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </Grow>
      ))}

        {/* Modern Add Column Button */}
        <Grow in={true} timeout={600}>
          <Box
            sx={{
              minWidth: 340,
              maxWidth: 340,
              flex: 1
            }}
          >
            {showAddColumn ? (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: 'fit-content',
                  minHeight: 650,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.85)})`,
                  backdropFilter: 'blur(10px)',
                  border: `2px dashed ${theme.palette.primary.main}`,
                  borderRadius: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.02)
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Add New Column
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter column name..."
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddColumn()}
                  autoFocus
                  size="small"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddColumn}
                    disabled={!newColumnName.trim()}
                    startIcon={<Add />}
                    sx={{ borderRadius: 2 }}
                  >
                    Add Column
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setShowAddColumn(false);
                      setNewColumnName('');
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Paper>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: 'fit-content',
                  minHeight: 650,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.85)})`,
                  backdropFilter: 'blur(10px)',
                  border: `2px dashed ${alpha(theme.palette.divider, 0.3)}`,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    transform: 'translateY(-2px)',
                    '& .add-icon': {
                      transform: 'scale(1.2) rotate(90deg)',
                    }
                  }
                }}
                onClick={() => setShowAddColumn(true)}
              >
                              <Box sx={{ textAlign: 'center' }}>
                  <Add 
                    className="add-icon"
                    sx={{ 
                      fontSize: 48, 
                      color: 'text.secondary', 
                      mb: 1,
                      transition: 'all 0.3s ease'
                    }} 
                  />
                  <Typography variant="body2" color="text.secondary">
                    Add Column
                  </Typography>
                </Box>
              </Paper>
            )}
          </Box>
        </Grow>
      </Box>
        </>
      )}

      {/* Comments Dialog */}
      <Dialog
        open={commentsDialogOpen}
        onClose={() => {
          setCommentsDialogOpen(false);
          setCommentsDialogIssue(null);
          setIssueComments([]);
          setNewComment('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {commentsDialogIssue
            ? `Comments • ${getIssueKeyDisplay(commentsDialogIssue)}`
            : 'Comments'}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 420 }}>
          {commentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : issueComments.length === 0 ? (
            <Box sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No comments yet. Be the first to add one.
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {issueComments.map((comment) => (
                <React.Fragment key={comment.id}>
                  <ListItem alignItems="flex-start" sx={{ py: 1 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">
                            {comment.author?.fullName || comment.author?.firstName || 'User'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(comment.updatedAt || comment.createdAt || Date.now()).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {comment.content}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Add a comment"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCommentsDialogOpen(false);
              setCommentsDialogIssue(null);
              setIssueComments([]);
              setNewComment('');
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleAddComment}
            variant="contained"
            disabled={!newComment.trim() || commentsLoading}
          >
            Add Comment
          </Button>
        </DialogActions>
      </Dialog>

      {/* RAG Settings Dialog */}
      <Dialog
        open={ragSettingsDialogOpen}
        onClose={() => {
          if (savingRagSettings) return;
          setRagSettingsDialogOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>RAG Settings</DialogTitle>
        <DialogContent dividers>
          <FormControlLabel
            control={
              <Switch
                checked={ragSettingsForm.enabled}
                onChange={(e) =>
                  setRagSettingsForm((prev) => ({ ...prev, enabled: e.target.checked }))
                }
              />
            }
            label="Enable RAG highlighting"
          />
          <TextField
            fullWidth
            type="number"
            label="Amber threshold (days before due date)"
            value={ragSettingsForm.amberThresholdDays}
            onChange={(e) =>
              setRagSettingsForm((prev) => ({ ...prev, amberThresholdDays: e.target.value }))
            }
            size="small"
            sx={{ mt: 2 }}
            inputProps={{ min: 0 }}
            helperText="Issues within this many days of the due date show as Amber."
          />
          <TextField
            fullWidth
            type="number"
            label="Red grace period (days after due date)"
            value={ragSettingsForm.redGracePeriodDays}
            onChange={(e) =>
              setRagSettingsForm((prev) => ({ ...prev, redGracePeriodDays: e.target.value }))
            }
            size="small"
            sx={{ mt: 2 }}
            inputProps={{ min: 0 }}
            helperText="Issues overdue by more than this many days show as Red."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRagSettingsDialogOpen(false)} disabled={savingRagSettings}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRagSettings}
            disabled={savingRagSettings}
            startIcon={savingRagSettings ? <CircularProgress size={18} /> : undefined}
          >
            {savingRagSettings ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog 
        open={filterDialogOpen} 
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Filter Issues
            </Typography>
            <Button
              startIcon={<Clear />}
              onClick={() => setFilters({
                assignees: [],
                priorities: [],
                types: [],
                statuses: [],
                search: '',
                dueDateFrom: '',
              dueDateTo: '',
              ragStatuses: []
              })}
              size="small"
            >
              Clear All
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Assignees</InputLabel>
                <Select
                  multiple
                  value={filters.assignees}
                  onChange={(e) => setFilters(prev => ({ ...prev, assignees: e.target.value as string[] }))}
                  label="Assignees"
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar src={user.avatar} sx={{ width: 24, height: 24 }}>
                          {user.firstName?.[0]}{user.lastName?.[0]}
                        </Avatar>
                        {user.firstName} {user.lastName}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Priorities</InputLabel>
                <Select
                  multiple
                  value={filters.priorities}
                  onChange={(e) => setFilters(prev => ({ ...prev, priorities: e.target.value as string[] }))}
                  label="Priorities"
                >
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Issue Types</InputLabel>
                <Select
                  multiple
                  value={filters.types}
                  onChange={(e) => setFilters(prev => ({ ...prev, types: e.target.value as string[] }))}
                  label="Issue Types"
                  renderValue={(selected) => (selected as string[]).join(', ')}
                >
                  <MenuItem value={IssueType.STORY}>Story</MenuItem>
                  <MenuItem value={IssueType.TASK}>Task</MenuItem>
                  <MenuItem value={IssueType.SUBTASK}>Subtask</MenuItem>
                  <MenuItem value={IssueType.BUG}>Bug</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Statuses</InputLabel>
                <Select
                  multiple
                  value={filters.statuses}
                  onChange={(e) => setFilters(prev => ({ ...prev, statuses: e.target.value as string[] }))}
                  label="Statuses"
                >
                  <MenuItem value="todo">To Do</MenuItem>
                  <MenuItem value="in-progress">In Progress</MenuItem>
                  <MenuItem value="review">Review</MenuItem>
                  <MenuItem value="done">Done</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                RAG Status
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {ragStatusOptions.map(option => {
                  const isSelected = filters.ragStatuses.includes(option.value);
                  return (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onClick={() => handleToggleRagStatus(option.value)}
                      variant={isSelected ? 'filled' : 'outlined'}
                      size="small"
                      sx={{
                        borderRadius: 2,
                        px: 1,
                        borderColor: alpha(option.color, 0.6),
                        backgroundColor: isSelected ? option.color : 'inherit',
                        color: isSelected ? theme.palette.getContrastText(option.color) : option.color,
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: isSelected ? option.color : alpha(option.color, 0.12),
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Grid>

            {/* Due Date Range Filter */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Due Date Range
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="From Date"
                value={filters.dueDateFrom}
                onChange={(e) => setFilters(prev => ({ ...prev, dueDateFrom: e.target.value }))}
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="To Date"
                value={filters.dueDateTo}
                onChange={(e) => setFilters(prev => ({ ...prev, dueDateTo: e.target.value }))}
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => setFilterDialogOpen(false)}
            sx={{
              borderColor: '#64748b',
              color: '#334155',
              '&:hover': {
                borderColor: '#334155',
                backgroundColor: 'rgba(51, 65, 85, 0.04)',
              }
            }}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Issue Modal (shared) */}
      {projectId && (
        <CreateIssueModal
          open={showAddStoryModal}
          onClose={() => setShowAddStoryModal(false)}
          projectId={projectId}
          boardType={board?.type}
          defaultValues={{
            type: IssueType.STORY,
            status: 'backlog',
            priority: Priority.MEDIUM,
            sprintId: currentSprint?.id && currentSprint.id !== 'default-sprint' ? currentSprint.id : undefined,
            epicId: currentFeature?.id && currentFeature.id !== 'default-feature' ? currentFeature.id : undefined,
            // Ensure issues created from the board are visible by linking them to this board
            customFields: {
              boardId: board?.id,
              columnId: (board?.columns || []).find((c: any) => c.status === 'backlog')?.id
            } as any
          } as any}
          onCreated={handleCreated}
          availableUsers={users}
        />
      )}

      {/* Issue Details Modal */}
      <IssueDetailsModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedIssue(null);
        }}
        issue={selectedIssue}
        onUpdate={handleIssueUpdate}
        onOpenIssue={(issue) => {
          setSelectedIssue(issue);
          setModalOpen(true);
        }}
        boardColumns={board?.columns || []}
        availableUsers={users}
        parentIssue={selectedIssue?.parent || null}
      />

      {/* Sprint Timeline Dialog */}
      <Dialog 
        open={timelineDialogOpen} 
        onClose={() => setTimelineDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {board?.type === 'sprint' && sprintDetailsForTimeline
                ? `${board?.name || 'Sprint'} Timeline (${sprintDetailsForTimeline.name})`
                : `${board?.name || 'Board'} Timeline`}
            </Typography>
            <Button onClick={() => setTimelineDialogOpen(false)} size="small">Close</Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          overflowX: 'auto',
          overflowY: 'visible',
          padding: '16px !important',
          '&::-webkit-scrollbar': {
            height: '10px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: alpha(theme.palette.divider, 0.1),
            borderRadius: '5px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.primary.main, 0.4),
            borderRadius: '5px',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.6),
            },
          },
        }}>
          {(() => {
            // Determine board type: if we have sprint details, it's definitely a sprint board
            const timelineSprint = sprintDetailsForTimeline || undefined;
            const timelineIssues = issues;
            // If we have sprint data, it MUST be a sprint board, regardless of board.type
            const timelineBoardType = timelineSprint ? 'sprint' : (board?.type || 'kanban');
            const timelineBoardName = board?.name || 'Board';
            
            console.log('🔍 Timeline Debug - What is being passed:');
            console.log('- board?.type:', board?.type);
            console.log('- sprintDetailsForTimeline:', timelineSprint);
            console.log('- timelineSprint?.name:', timelineSprint?.name);
            console.log('- timelineSprint?.startDate:', timelineSprint?.startDate);
            console.log('- timelineSprint?.endDate:', timelineSprint?.endDate);
            console.log('- issues count:', timelineIssues?.length || 0);
            console.log('- boardType passed:', timelineBoardType);
            console.log('- boardName passed:', timelineBoardName);
            console.log('- First few issues:', timelineIssues?.slice(0, 3)?.map(i => ({ id: i.id, title: i.title, dueDate: i.dueDate, sprintId: i.sprintId })));
            
            return (
              <SprintTimelineView 
                sprint={timelineSprint}
                issues={timelineIssues}
                boardType={timelineBoardType}
                boardName={timelineBoardName}
                height={600}
              />
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Manage Members Modal */}
      <Dialog open={showAddMemberModal} onClose={handleCloseAddMemberModal} maxWidth="md" fullWidth>
        <DialogTitle>
          Manage Board Members
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Current Board Members */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Current Board Members ({users.length})
              </Typography>
              {users.length === 0 ? (
                <Alert severity="info">
                  No board members found. Add members from the project team below.
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {users.map((user) => {
                    const issueCount = issues.filter(issue => 
                      issue.assigneeId === user.id || 
                      (issue.assignments && issue.assignments.some(assignment => assignment.user?.id === user.id))
                    ).length;
                    
                    return (
                      <Box
                        key={user.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 2,
                          border: '1px solid',
                          borderColor: 'grey.300',
                          borderRadius: 2,
                          backgroundColor: 'grey.50'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Tooltip title={`${user.firstName} ${user.lastName}`} arrow>
                            <Avatar sx={{ width: 40, height: 40 }}>
                              {user.firstName?.[0]}{user.lastName?.[0]}
                            </Avatar>
                          </Tooltip>
                          <Box>
                            <Typography variant="body1" fontWeight={500}>
                              {user.firstName} {user.lastName}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {user.email} • {issueCount} issues assigned
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveMember(user.id)}
                          title="Remove from board"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Available Users to Add */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Add Project Members to Board
              </Typography>
              {availableUsers.length === 0 ? (
                <Alert severity="info">
                  No available users to add. All project members are already board members.
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {availableUsers.map((user) => (
                    <Box
                      key={user.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 2,
                        backgroundColor: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.50',
                          borderColor: 'primary.main'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Tooltip title={`${user.firstName} ${user.lastName}`} arrow>
                          <Avatar sx={{ width: 40, height: 40 }}>
                            {user.firstName?.[0]}{user.lastName?.[0]}
                          </Avatar>
                        </Tooltip>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleAddMemberSubmit(user.id)}
                        startIcon={<Add />}
                        sx={{
                          borderColor: '#64748b',
                          color: '#334155',
                          '&:hover': {
                            borderColor: '#334155',
                            backgroundColor: 'rgba(51, 65, 85, 0.04)',
                          }
                        }}
                      >
                        Add to Board
                      </Button>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddMemberModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </Layout>
  );
};

export default Board;